import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './App.css';
import LanguageSwitcher from './components/LanguageSwitcher';

const API_URL = 'https://sr1trq6sta.execute-api.us-east-1.amazonaws.com/prod';

// Helper function to decode JWT token and extract role information
function decodeJWTToken(token) {
  if (!token) return null;
  
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return decoded;
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return null;
  }
}

// Get user roles from JWT token
function getUserRoles() {
  const token = localStorage.getItem('token');
  const decoded = decodeJWTToken(token);
  
  if (!decoded) return { isAdmin: false, isExecute: false, isMod: false };
  
  return {
    isAdmin: decoded.isAdmin || false,
    isExecute: decoded.isExecute || false,
    isMod: decoded.isMod || false
  };
}

// Avatar component for displaying user profile images
const Avatar = ({ user, size = 'medium', className = '' }) => {
  const sizeClasses = {
    small: 'w-10 h-10 text-sm',
    medium: 'w-14 h-14 text-base', 
    large: 'w-20 h-20 text-xl'
  };

  const avatarPhoto = user?.photos && user.photos.length > 0 ? user.photos[0] : null;
  const initials = (user?.name || user?.email || 'U').charAt(0).toUpperCase();
  

  if (avatarPhoto) {
    return (
      <div className={`avatar-container ${sizeClasses[size]} ${className}`}>
        <img 
          src={avatarPhoto} 
          alt={`${user.name || 'User'} avatar`}
          className="avatar-image"
          onError={(e) => {
            // Fallback to initials if image fails to load
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
        <div className="avatar-fallback" style={{display: 'none'}}>
          {initials}
        </div>
      </div>
    );
  }

  return (
    <div className={`avatar-fallback ${sizeClasses[size]} ${className}`}>
      {initials}
    </div>
  );
};

function App() {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState('home');
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [viewingProfile, setViewingProfile] = useState(null);
  
  // Authentication state
  const [user, setUser] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'register'
  const [authMessage, setAuthMessage] = useState('');
  const [authMessageType, setAuthMessageType] = useState('');
  const [showRegisterProfile, setShowRegisterProfile] = useState(false);

  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Authentication form data
  const [authData, setAuthData] = useState({
    email: '',
    password: '',
    name: ''
  });
  const [showPassword, setShowPassword] = useState(false);

  // Edit profile state
  const [editingProfile, setEditingProfile] = useState(null);
  const [editFormData, setEditFormData] = useState({
    gender: '',
    age: '',
    height: '',
    weight: '',
    maritalStatus: '',
    career: '',
    personality: '',
    education: '',
    languages: '',
    interests: '',
    bornPlace: '',
    location: '',
    expectation: '',
    phone: ''
  });

  // Register profile form data (combines auth + profile data)
  const [registerProfileData, setRegisterProfileData] = useState({
    // Auth fields
    name: '',
    email: '',
    password: '',
    // Profile fields
    gender: '',
    age: '',
    height: '',
    weight: '',
    maritalStatus: '',
    career: '',
    personality: '',
    education: '',
    languages: '',
    interests: '',
    bornPlace: '',
    location: '',
    expectation: '',
    phone: ''
  });

  // User's own profile state (separate from filtered profiles for discovery)
  const [userProfile, setUserProfile] = useState(null);
  const [userProfileLoading, setUserProfileLoading] = useState(false);

  // All users for executive management (including admin/execute/mod users)
  const [allUsers, setAllUsers] = useState([]);
  const [allUsersLoading, setAllUsersLoading] = useState(false);

  // User management state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchDebounceTimer, setSearchDebounceTimer] = useState(null);

  // Posts/feed state
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [newPostContent, setNewPostContent] = useState('');
  const [newPostMedia, setNewPostMedia] = useState([]);
  const [editingPost, setEditingPost] = useState(null);
  const [editPostContent, setEditPostContent] = useState('');

  // Matching state
  const [matches, setMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(false);


  // Comments and reactions state
  const [commentsClipPost, setCommentsClipPost] = useState(null);
  const [newComment, setNewComment] = useState({});

  // Scroll detection state
  const [isScrolled, setIsScrolled] = useState(false);
  const [postReactions, setPostReactions] = useState({});
  const [postComments, setPostComments] = useState({});
  
  // User profiles cache for avatars
  const [userProfilesCache, setUserProfilesCache] = useState({});
  
  // Refs to track if data has been fetched for current tab to prevent re-fetching
  const fetchedTabsRef = useRef(new Set());
  
  // Ref to track recent error messages to prevent spam
  const recentErrorsRef = useRef(new Set());
  
  // Ref to track if fetchUserProfile is in progress
  const fetchingUserProfileRef = useRef(false);
  
  // Global ref to track all ongoing API calls to prevent too many simultaneous requests
  const ongoingApiCallsRef = useRef(new Set());
  
  // Ref to track last error toast time for global rate limiting
  const lastErrorTimeRef = useRef(0);

  // Define all callback functions first
  const fetchUserProfile = useCallback(async () => {
    if (!user?.email && !user?.userId) {
      return;
    }
    
    // Prevent multiple concurrent calls
    if (fetchingUserProfileRef.current || userProfileLoading) {
      return;
    }
    
    fetchingUserProfileRef.current = true;
    setUserProfileLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      } else {
        // No token available, user needs to login first
        console.warn('fetchUserProfile: No token found, skipping profile fetch');
        setUserProfileLoading(false);
        fetchingUserProfileRef.current = false;
        return;
      }
      
      const response = await fetch(`${API_URL}/profiles`, { headers });
      const data = await response.json();
      
      if (response.ok) {
        // Find profile by userId (most reliable) with email fallback for backwards compatibility
        const foundProfile = (data.profiles || []).find(profile => {
          // Primary: Match by userId (always unique and available)
          if (user.userId && profile.userId) {
            return profile.userId === user.userId;
          }
          // Fallback: Match by email for backwards compatibility
          if (user.email && profile.email) {
            return profile.email === user.email;
          }
          return false;
        });
        setUserProfile(foundProfile);
        
        // Return the profile so it can be used for conditional logic
        return foundProfile;
      } else if (response.status === 401) {
        // Token might be expired, clear it
        console.warn('Authentication failed - user may need to log in again');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        showErrorToast('Session expired. Please log in again.');
      } else {
        showErrorToast('Failed to fetch user profile');
      }
    } catch (error) {
      showErrorToast('Error connecting to server (fetchUserProfile)');
    } finally {
      setUserProfileLoading(false);
      fetchingUserProfileRef.current = false;
    }
    
    return null;
  }, [user?.email, user?.userId, userProfileLoading]);

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      } else {
        // No token available, user needs to login first
        console.warn('fetchProfiles: No token found, skipping profiles fetch');
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${API_URL}/profiles`, { headers });
      const data = await response.json();
      
      if (response.ok) {
        const allProfiles = data.profiles || [];
        
        // Filter profiles based on user role and gender
        const userRoles = getUserRoles();
        const filteredProfiles = allProfiles.filter(profile => {
          // Exclude current user
          if (user && profile.email === user.email) return false;
          
          // Exclude ALL staff users (admin, execute, mod) from discovery page for everyone
          if (profile.isAdmin || profile.isExecute || profile.isMod) return false;
          
          // Staff users (admin, execute, mod) can see all regular users without gender filtering
          if (userRoles.isAdmin || userRoles.isExecute || userRoles.isMod) {
            return true; // Staff can see all regular users
          }
          
          // Gender-based filtering for regular users only
          if (userProfile?.gender && profile.gender) {
            const currentUserGender = userProfile.gender.toLowerCase();
            const profileGender = profile.gender.toLowerCase();
            
            // Men only see women, women only see men
            if (currentUserGender === 'male' && profileGender !== 'female') return false;
            if (currentUserGender === 'female' && profileGender !== 'male') return false;
          } else {
            // If either user doesn't have gender set, don't show the profile
            // This ensures strict gender filtering
            return false;
          }
          
          // If gender filtering passes, show the profile
          return true;
        });
        
        setProfiles(filteredProfiles);
      } else if (response.status === 401) {
        console.warn('Authentication failed - user may need to log in again');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        showErrorToast('Session expired. Please log in again.');
      } else {
        console.warn(`Failed to fetch profiles: ${response.status}`);
        showErrorToast('Failed to fetch profiles. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching profiles:', error);
      showErrorToast('Error connecting to server. Please check your connection and try again.');
    }
    setLoading(false);
  }, [user, userProfile]);

  const fetchAllUsers = useCallback(async () => {
    setAllUsersLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      } else {
        // No token available, user needs to login first
        console.warn('fetchAllUsers: No token found, skipping users fetch');
        setAllUsersLoading(false);
        return;
      }
      
      const response = await fetch(`${API_URL}/profiles`, { headers });
      const data = await response.json();
      
      if (response.ok) {
        // Filter users based on role-based access control
        const userRoles = getUserRoles();
        const allUserProfiles = (data.profiles || []).filter(profile => {
          // Exclude current user from management list
          if (user && profile.email === user.email) return false;
          
          // Role-based access control: admin and mod users cannot see executive users
          if ((userRoles.isAdmin || userRoles.isMod) && !userRoles.isExecute) {
            // Admin and mod users (who are not executives) cannot see executive users
            if (profile.isExecute) return false;
          }
          
          return true;
        });
        setAllUsers(allUserProfiles);
      } else if (response.status === 401) {
        console.warn('Authentication failed - user may need to log in again');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        showErrorToast('Session expired. Please log in again.');
      } else {
        showErrorToast('Failed to fetch users');
      }
    } catch (error) {
      showErrorToast('Error connecting to server (fetchAllUsers)');
    }
    setAllUsersLoading(false);
  }, [user, userProfile]);

  const fetchPosts = useCallback(async () => {
    setPostsLoading(true);
    try {
      const response = await fetch(`${API_URL}/posts`);
      const data = await response.json();
      
      if (response.ok) {
        setPosts(data.posts || []);
      } else {
        showErrorToast('Failed to fetch posts');
      }
    } catch (error) {
      showErrorToast('Error connecting to server (fetchPosts)');
    }
    setPostsLoading(false);
  }, []);

  // Image compression function
  const compressImage = (file, maxSizeKB = 1024, quality = 0.8) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;
        const maxDimension = 1920; // Max width/height
        
        if (width > height && width > maxDimension) {
          height = (height * maxDimension) / width;
          width = maxDimension;
        } else if (height > maxDimension) {
          width = (width * maxDimension) / height;
          height = maxDimension;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        
        // Try different quality levels to get under size limit
        const tryCompress = (currentQuality) => {
          canvas.toBlob((blob) => {
            const sizeKB = blob.size / 1024;
            
            if (sizeKB <= maxSizeKB || currentQuality <= 0.1) {
              // Convert blob to base64
              const reader = new FileReader();
              reader.onload = (e) => {
                resolve({
                  blob,
                  base64: e.target.result.split(',')[1],
                  preview: e.target.result,
                  compressed: sizeKB < file.size / 1024
                });
              };
              reader.readAsDataURL(blob);
            } else {
              // Try with lower quality
              tryCompress(currentQuality - 0.1);
            }
          }, file.type === 'image/png' ? 'image/png' : 'image/jpeg', currentQuality);
        };
        
        tryCompress(quality);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };


  // Handle media file selection
  const handleMediaSelect = async (e) => {
    const files = Array.from(e.target.files);
    const maxFiles = 4;
    const maxImageSize = 10 * 1024 * 1024; // 10MB for images
    
    if (newPostMedia.length + files.length > maxFiles) {
      showToast(t('posts.maxFilesExceeded', { max: maxFiles }), 'error');
      return;
    }
    
    const validFiles = files.filter(file => {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      
      if (!isImage && !isVideo) {
        showToast(t('posts.fileTypeNotSupported', { filename: file.name }), 'error');
        return false;
      }
      
      // Check size limits based on file type
      if (isImage && file.size > maxImageSize) {
        showToast(t('posts.fileSizeExceeded', { filename: file.name, size: 10 }), 'error');
        return false;
      }
      
      
      return true;
    });
    
    // Process files (compress images, convert to base64)
    for (const file of validFiles) {
      try {
        if (file.type.startsWith('image/')) {
          // Compress image if it's too large
          if (file.size > maxImageSize) {
            showToast(`Compressing ${file.name}...`, 'info');
            const compressed = await compressImage(file, 8192); // Target ~8MB max
            
            const mediaItem = {
              id: Date.now() + Math.random(),
              file: file,
              type: 'image',
              name: file.name,
              base64: compressed.base64,
              preview: compressed.preview,
              compressed: compressed.compressed
            };
            setNewPostMedia(prev => [...prev, mediaItem]);
            
            if (compressed.compressed) {
              showToast(`${file.name} compressed successfully`, 'success');
            }
          } else {
            // Image is already small enough, just convert to base64
            const reader = new FileReader();
            reader.onload = (e) => {
              const mediaItem = {
                id: Date.now() + Math.random(),
                file: file,
                type: 'image',
                name: file.name,
                base64: e.target.result.split(',')[1],
                preview: e.target.result,
                compressed: false
              };
              setNewPostMedia(prev => [...prev, mediaItem]);
            };
            reader.readAsDataURL(file);
          }
        } else {
          // Video file - convert to base64 directly (no compression)
          const reader = new FileReader();
          reader.onload = (e) => {
            const mediaItem = {
              id: Date.now() + Math.random(),
              file: file,
              type: 'video',
              name: file.name,
              base64: e.target.result.split(',')[1],
              preview: e.target.result,
              compressed: false
            };
            setNewPostMedia(prev => [...prev, mediaItem]);
          };
          reader.readAsDataURL(file);
        }
      } catch (error) {
        console.error('File processing error:', error);
        showToast(`Error processing ${file.name}`, 'error');
      }
    }
  };
  
  // Remove media item
  const removeMediaItem = (id) => {
    setNewPostMedia(prev => prev.filter(item => item.id !== id));
  };

  const createPost = async (e) => {
    e.preventDefault();
    
    if (!newPostContent.trim() && newPostMedia.length === 0) {
      showToast(t('posts.pleaseAddContent'), 'error');
      return;
    }

    if (!user) {
      showToast(t('messages.mustBeLoggedIn'), 'error');
      return;
    }

    setLoading(true);
    try {
      // First upload media if any
      let mediaUrls = [];
      if (newPostMedia.length > 0) {
        for (const mediaItem of newPostMedia) {
          try {
            
            const uploadResponse = await fetch(`${API_URL}/photos?type=post`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                userId: user.userId,
                photoBase64: mediaItem.base64,
                fileName: mediaItem.name,
                mediaType: mediaItem.type // Add media type for backend processing
              })
            });
            
            const uploadData = await uploadResponse.json();
            if (uploadResponse.ok) {
              mediaUrls.push({
                url: uploadData.mediaUrl,
                type: mediaItem.type
              });
            } else {
              // Better error messages based on status code
              if (uploadResponse.status === 413) {
                showToast(`${mediaItem.name} is too large for upload. Please compress it further.`, 'error');
              } else if (uploadResponse.status === 400) {
                showToast(`Upload failed for ${mediaItem.name}: ${uploadData.message || 'Invalid file format'}`, 'error');
              } else {
                showToast(t('posts.uploadFailed', { filename: mediaItem.name }), 'error');
              }
            }
          } catch (error) {
            console.error('Media upload error:', error);
            if (error.name === 'TypeError' && error.message.includes('fetch')) {
              showToast(`Network error uploading ${mediaItem.name}. Please check your connection.`, 'error');
            } else {
              showToast(t('posts.uploadError', { filename: mediaItem.name }), 'error');
            }
          }
        }
      }
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userId: user.userId,
          content: newPostContent,
          media: mediaUrls
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        showToast(t('posts.postCreatedSuccess'), 'success');
        setNewPostContent('');
        setNewPostMedia([]);
        setShowCreatePost(false);
        fetchPosts(); // Refresh posts
      } else {
        showToast(data.message || 'Failed to create post', 'error');
      }
    } catch (error) {
      showErrorToast('Error connecting to server');
    }
    setLoading(false);
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/posts/${postId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (response.ok) {
        // showToast('Post deleted successfully!', 'success');
        fetchPosts(); // Refresh posts
      } else {
        showToast(data.message || 'Failed to delete post', 'error');
      }
    } catch (error) {
      showToast('Error deleting post', 'error');
    }
  };

  const startEditingPost = (post) => {
    setEditingPost(post);
    setEditPostContent(post.content);
  };

  const updatePost = async (e) => {
    e.preventDefault();
    
    if (!editPostContent.trim()) {
      showToast('Please enter some content for your post', 'error');
      return;
    }

    if (!editingPost) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/posts/${editingPost.postId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content: editPostContent,
          userId: user.userId
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        // showToast('Post updated successfully!', 'success');
        setEditPostContent('');
        setEditingPost(null);
        fetchPosts(); // Refresh posts
      } else {
        showToast(data.message || 'Failed to update post', 'error');
      }
    } catch (error) {
      showErrorToast('Error connecting to server');
    }
    setLoading(false);
  };

  const isYouTubeUrl = (url) => {
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/;
    return youtubeRegex.test(url);
  };

  const isYouTubeShorts = (url) => {
    return url.includes('/shorts/') || (url.includes('youtu.be/') && !url.includes('watch'));
  };

  const isFacebookReel = (url) => {
    return url.includes('facebook.com') && (url.includes('/share/r/') || url.includes('/reel/') || url.includes('/plugins/video.php'));
  };

  const extractFacebookReelUrl = (url) => {
    // Handle video plugin URLs
    if (url.includes('/plugins/video.php')) {
      // Extract the href parameter from URL like: https://www.facebook.com/plugins/video.php?height=476&href=https%3A%2F%2Fwww.facebook.com%2Freel%2F2233410273771166%2F
      const match = url.match(/href=([^&]+)/);
      if (match) {
        const decodedUrl = decodeURIComponent(match[1]);
        return decodedUrl;
      }
    }
    // Handle share links
    else if (url.includes('/share/r/')) {
      // Extract the reel ID from URL like: https://www.facebook.com/share/r/19ttG3w3Wa/?mibextid=wwXIfr
      const match = url.match(/\/share\/r\/([^/?]+)/);
      if (match) {
        return `https://www.facebook.com/reel/${match[1]}/`;
      }
    } 
    // Handle direct reel links
    else if (url.includes('/reel/')) {
      // Extract from direct reel URL like: https://www.facebook.com/reel/2233410273771166/
      const match = url.match(/\/reel\/([^/?]+)/);
      if (match) {
        return `https://www.facebook.com/reel/${match[1]}/`;
      }
    }
    return null;
  };

  const extractYouTubeId = (url) => {
    // Handle various YouTube URL formats including Shorts
    const patterns = [
      // YouTube Shorts: https://youtu.be/VIDEO_ID or https://youtube.com/shorts/VIDEO_ID
      /(?:youtu\.be\/|youtube\.com\/shorts\/)([^"&?/\s]{11})/,
      // Regular YouTube: https://youtube.com/watch?v=VIDEO_ID
      /youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)([^"&?/\s]{11})/,
      // YouTube short URLs with parameters: https://youtu.be/VIDEO_ID?si=...
      /youtu\.be\/([^"&?/\s]{11})/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    
    return null;
  };

  const formatPostContent = (content) => {
    // Clean up content and handle newlines
    const cleanContent = content.trim();
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = cleanContent.split(urlRegex);
    
    return parts.map((part, index) => {
      // Clean up each part to remove trailing whitespace/newlines
      const cleanPart = part.trim();
      if (urlRegex.test(cleanPart)) {
        if (isYouTubeUrl(cleanPart)) {
          const videoId = extractYouTubeId(cleanPart);
          if (videoId) {
            // Handle YouTube Shorts - embed with vertical aspect ratio
            if (isYouTubeShorts(cleanPart)) {
              return (
                <div key={index} className="youtube-shorts-embed">
                  <iframe
                    src={`https://www.youtube.com/embed/${videoId}`}
                    title="YouTube Shorts"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              );
            } else {
              // Regular YouTube video - embed normally
              return (
                <div key={index} className="youtube-embed">
                  <iframe
                    src={`https://www.youtube.com/embed/${videoId}`}
                    title="YouTube video"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              );
            }
          }
        }
        
        // Handle Facebook Reels - embed using Facebook's video plugin
        if (isFacebookReel(cleanPart)) {
          const reelUrl = extractFacebookReelUrl(cleanPart);
          if (reelUrl) {
            const encodedUrl = encodeURIComponent(reelUrl);
            return (
              <div key={index} className="facebook-reel-embed">
                <iframe 
                  src={`https://www.facebook.com/plugins/video.php?height=476&href=${encodedUrl}&show_text=false&width=267&t=0`}
                  width="267" 
                  height="476" 
                  style={{border: 'none', overflow: 'hidden'}} 
                  allowFullScreen={true}
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                  title="Facebook Reel"
                />
              </div>
            );
          }
        }
        
        return (
          <a key={index} href={cleanPart} target="_blank" rel="noopener noreferrer" className="post-link">
            {cleanPart}
          </a>
        );
      }
      return part;
    });
  };

  const generateMatches = useCallback(async () => {
    setMatchesLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      } else {
        // No token available, user needs to login first
        console.warn('generateMatches: No token found, skipping matches generation');
        setMatchesLoading(false);
        return;
      }
      
      const response = await fetch(`${API_URL}/profiles`, { headers });
      const data = await response.json();
      
      if (response.ok) {
        const allProfiles = data.profiles || [];
        
        // Filter out admin, execute, and mod users from matching
        const regularProfiles = allProfiles.filter(profile => 
          !profile.isAdmin && !profile.isExecute && !profile.isMod
        );
        
        // Flexible matching algorithm now active
        
        const potentialMatches = calculateMatches(regularProfiles);
        setMatches(potentialMatches);
      } else if (response.status === 401) {
        console.warn('Authentication failed - user may need to log in again');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        showToast('Session expired. Please log in again.', 'error');
      } else {
        showToast('Failed to generate matches', 'error');
      }
    } catch (error) {
      showToast('Error generating matches', 'error');
    }
    setMatchesLoading(false);
  }, []);

  const calculateMatches = (profiles) => {
    const matches = [];
    
    if (!Array.isArray(profiles) || profiles.length < 2) {
      return matches;
    }
    
    
    // Create all possible pairs
    for (let i = 0; i < profiles.length; i++) {
      for (let j = i + 1; j < profiles.length; j++) {
        const user1 = profiles[i];
        const user2 = profiles[j];
        
        // Skip if either user doesn't have enough profile data
        if (!user1 || !user2 || !user1.userId || !user2.userId) {
          continue;
        }
        
        // Check if users have minimum required data for flexible matching
        // Gender is mandatory, plus at least one other meaningful field
        if (!user1.gender || !user2.gender) {
          continue;
        }
        
        // Helper function to check if a user has meaningful profile data
        const hasMatchableData = (user) => {
          const hasInterests = user.interests && (
            (typeof user.interests === 'string' && user.interests.trim().length > 0) ||
            (Array.isArray(user.interests) && user.interests.length > 0)
          );
          const hasExpectation = user.expectation && user.expectation.trim().length > 0;
          const hasBio = user.bio && user.bio.trim().length > 0;
          const hasAge = user.age && user.age > 0;
          const hasLocation = user.location && user.location.trim().length > 0;
          const hasCareer = user.career && user.career.trim().length > 0;
          const hasEducation = user.education && user.education.trim().length > 0;
          const hasPersonality = user.personality && user.personality.trim().length > 0;
          
          // Count meaningful fields (excluding gender which is mandatory)
          const meaningfulFields = [
            hasInterests, hasExpectation, hasBio, hasAge, hasLocation, 
            hasCareer, hasEducation, hasPersonality
          ].filter(Boolean).length;
          
          return meaningfulFields >= 1; // At least one meaningful field beyond gender
        };
        
        if (!hasMatchableData(user1) || !hasMatchableData(user2)) {
          continue;
        }
        
        // Only match women with men (heterosexual matching)
        const isValidGenderPair = (user1.gender.toLowerCase() === 'female' && user2.gender.toLowerCase() === 'male') ||
                                 (user1.gender.toLowerCase() === 'male' && user2.gender.toLowerCase() === 'female');
        
        if (!isValidGenderPair) {
          continue;
        }
        
        try {
          const compatibility = calculateCompatibility(user1, user2);
          
          if (compatibility && compatibility.score > 0) {
            matches.push({
              id: `${user1.userId}-${user2.userId}`,
              user1,
              user2,
              compatibility
            });
          }
        } catch (error) {
          // Error calculating compatibility for users
        }
      }
    }
    
    // Sort by compatibility score (highest first)
    return matches.sort((a, b) => (b.compatibility?.score || 0) - (a.compatibility?.score || 0));
  };

  const calculateCompatibility = (user1, user2) => {
    if (!user1 || !user2) {
      return { score: 0, factors: [] };
    }
    
    let totalScore = 0;
    let totalWeight = 0;
    const factors = [];
    
    try {
      // Define compatibility checks with their weights
      const compatibilityChecks = [
        {
          name: 'interests',
          weight: 0.35,
          calculate: () => calculateInterestCompatibility(user1.interests, user2.interests),
          label: (score) => `${Math.round(score * 100)}% shared interests`
        },
        {
          name: 'expectation',
          weight: 0.25,
          calculate: () => calculateExpectationCompatibility(
            user1.expectation || user1.bio, 
            user2.expectation || user2.bio
          ),
          label: (score) => `${Math.round(score * 100)}% profile similarity`
        },
        {
          name: 'age',
          weight: 0.20,
          calculate: () => calculateAgeCompatibility(user1.age, user2.age, user1, user2),
          label: (score) => `${Math.round(score * 100)}% age compatibility`
        },
        {
          name: 'career',
          weight: 0.10,
          calculate: () => calculateCareerCompatibility(user1.career, user2.career),
          label: (score) => 'Career compatibility'
        },
        {
          name: 'education',
          weight: 0.05,
          calculate: () => calculateEducationCompatibility(user1.education, user2.education),
          label: (score) => 'Education compatibility'
        },
        {
          name: 'location',
          weight: 0.05,
          calculate: () => calculateLocationCompatibility(user1.location, user2.location),
          label: (score) => 'Same location'
        }
      ];
      
      // Calculate scores only for available data, adjust weights dynamically
      for (const check of compatibilityChecks) {
        try {
          const score = check.calculate();
          if (score > 0) {
            totalScore += score * check.weight;
            totalWeight += check.weight;
            factors.push(check.label(score));
          } else {
            // For missing data, we still add partial weight to maintain fairness
            // This prevents users with fewer fields from being unfairly penalized
            const hasData1 = hasFieldData(user1, check.name);
            const hasData2 = hasFieldData(user2, check.name);
            
            if (hasData1 && hasData2) {
              // Both have data but no compatibility
              totalWeight += check.weight;
            } else if (hasData1 || hasData2) {
              // Only one has data - add partial weight
              totalWeight += check.weight * 0.3;
            }
            // If neither has data, don't penalize by adding weight
          }
        } catch (error) {
          console.warn(`Error calculating ${check.name} compatibility:`, error);
        }
      }
      
      // Calculate final score as percentage of available compatibility
      const finalScore = totalWeight > 0 ? (totalScore / totalWeight) : 0;
      
      // Add bonus factors for profile completeness
      const completeness1 = calculateProfileCompleteness(user1);
      const completeness2 = calculateProfileCompleteness(user2);
      const avgCompleteness = (completeness1 + completeness2) / 2;
      
      if (avgCompleteness > 0.7) {
        factors.push('Complete profiles');
      } else if (avgCompleteness > 0.5) {
        factors.push('Good profile details');
      }
      
      return {
        score: Math.round(finalScore * 100),
        factors,
        completeness: Math.round(avgCompleteness * 100)
      };
    } catch (error) {
      return { score: 0, factors: [] };
    }
  };
  
  // Helper function to check if user has data for a specific field
  const hasFieldData = (user, fieldName) => {
    switch (fieldName) {
      case 'interests':
        return user.interests && (
          (typeof user.interests === 'string' && user.interests.trim().length > 0) ||
          (Array.isArray(user.interests) && user.interests.length > 0)
        );
      case 'expectation':
        return (user.expectation && user.expectation.trim().length > 0) || 
               (user.bio && user.bio.trim().length > 0);
      case 'age':
        return user.age && user.age > 0;
      case 'career':
        return user.career && user.career.trim().length > 0;
      case 'education':
        return user.education && user.education.trim().length > 0;
      case 'location':
        return user.location && user.location.trim().length > 0;
      default:
        return false;
    }
  };
  
  // Helper function to calculate profile completeness
  const calculateProfileCompleteness = (user) => {
    const fields = ['interests', 'expectation', 'age', 'career', 'education', 'location', 'height', 'personality'];
    const filledFields = fields.filter(field => hasFieldData(user, field) || 
      (field === 'height' && user.height && user.height.trim().length > 0) ||
      (field === 'personality' && user.personality && user.personality.trim().length > 0)
    ).length;
    return filledFields / fields.length;
  };

  const calculateInterestCompatibility = (interests1, interests2) => {
    try {
      // Convert interests to arrays if they're strings
      let arr1, arr2;
      
      if (typeof interests1 === 'string') {
        arr1 = interests1.split(',').map(i => i.trim()).filter(i => i);
      } else if (Array.isArray(interests1)) {
        arr1 = interests1.filter(i => i);
      } else {
        return 0;
      }
      
      if (typeof interests2 === 'string') {
        arr2 = interests2.split(',').map(i => i.trim()).filter(i => i);
      } else if (Array.isArray(interests2)) {
        arr2 = interests2.filter(i => i);
      } else {
        return 0;
      }
      
      if (arr1.length === 0 || arr2.length === 0) {
        return 0;
      }
      
      const set1 = new Set(arr1.map(i => i.toLowerCase().trim()));
      const set2 = new Set(arr2.map(i => i.toLowerCase().trim()));
      
      if (set1.size === 0 || set2.size === 0) return 0;
      
      const intersection = new Set([...set1].filter(x => set2.has(x)));
      const union = new Set([...set1, ...set2]);
      
      return intersection.size / union.size; // Jaccard similarity
    } catch (error) {
      return 0;
    }
  };

  const calculateExpectationCompatibility = (expectation1, expectation2) => {
    if (!expectation1 || !expectation2 || 
        typeof expectation1 !== 'string' || typeof expectation2 !== 'string') {
      return 0;
    }
    
    try {
      const words1 = expectation1.toLowerCase().split(/\s+/).filter(word => word.length > 3);
      const words2 = expectation2.toLowerCase().split(/\s+/).filter(word => word.length > 3);
      
      if (words1.length === 0 || words2.length === 0) return 0;
      
      const set1 = new Set(words1);
      const set2 = new Set(words2);
      
      const intersection = new Set([...set1].filter(x => set2.has(x)));
      const union = new Set([...set1, ...set2]);
      
      return intersection.size / union.size;
    } catch (error) {
      return 0;
    }
  };

  const calculateAgeCompatibility = (age1, age2, user1, user2) => {
    if (!age1 || !age2 || typeof age1 !== 'number' || typeof age2 !== 'number') {
      return 0;
    }
    
    try {
      const ageDiff = Math.abs(age1 - age2);
      let baseScore = 0;
      
      // Base compatibility score based on age difference
      if (ageDiff <= 2) baseScore = 1;
      else if (ageDiff <= 5) baseScore = 0.8;
      else if (ageDiff <= 10) baseScore = 0.6;
      else if (ageDiff <= 15) baseScore = 0.4;
      else baseScore = 0.2;
      
      // Apply age preference bonus based on gender
      if (user1 && user2 && user1.gender && user2.gender) {
        const user1Gender = user1.gender.toLowerCase();
        const user2Gender = user2.gender.toLowerCase();
        
        // Bonus for preferred age dynamics:
        // - Men being older than women
        // - Women being older than men
        if ((user1Gender === 'male' && user2Gender === 'female' && age1 > age2) ||
            (user1Gender === 'female' && user2Gender === 'male' && age1 > age2) ||
            (user2Gender === 'male' && user1Gender === 'female' && age2 > age1) ||
            (user2Gender === 'female' && user1Gender === 'male' && age2 > age1)) {
          
          const ageDifference = Math.abs(age1 - age2);
          
          // Apply bonus based on age difference (sweet spot: 2-8 years)
          if (ageDifference >= 2 && ageDifference <= 8) {
            baseScore = Math.min(1.0, baseScore + 0.3); // Significant bonus
          } else if (ageDifference >= 1 && ageDifference <= 12) {
            baseScore = Math.min(1.0, baseScore + 0.15); // Moderate bonus
          }
        }
      }
      
      return baseScore;
    } catch (error) {
      return 0;
    }
  };

  const calculateLocationCompatibility = (location1, location2) => {
    if (!location1 || !location2 || 
        typeof location1 !== 'string' || typeof location2 !== 'string') {
      return 0;
    }
    
    try {
      const loc1 = location1.toLowerCase().trim();
      const loc2 = location2.toLowerCase().trim();
      
      if (loc1 === loc2) return 1;
      
      // Check if they share city or country
      const parts1 = loc1.split(',').map(p => p.trim());
      const parts2 = loc2.split(',').map(p => p.trim());
      
      for (const part1 of parts1) {
        for (const part2 of parts2) {
          if (part1 === part2 && part1.length > 0) return 0.5;
        }
      }
      
      return 0;
    } catch (error) {
      return 0;
    }
  };

  const calculateCareerCompatibility = (career1, career2) => {
    if (!career1 || !career2 || 
        typeof career1 !== 'string' || typeof career2 !== 'string') {
      return 0;
    }
    
    try {
      const c1 = career1.toLowerCase().trim();
      const c2 = career2.toLowerCase().trim();
      
      // Exact match
      if (c1 === c2) return 1;
      
      // Check for similar career fields
      const careerFields = [
        ['tech', 'technology', 'software', 'developer', 'engineer', 'programmer', 'it'],
        ['medical', 'doctor', 'nurse', 'healthcare', 'medicine', 'physician'],
        ['business', 'finance', 'marketing', 'sales', 'management', 'accounting'],
        ['education', 'teacher', 'professor', 'instructor', 'academic'],
        ['creative', 'artist', 'designer', 'writer', 'musician', 'photographer'],
        ['law', 'lawyer', 'legal', 'attorney', 'judge'],
        ['science', 'research', 'scientist', 'researcher', 'laboratory']
      ];
      
      for (const field of careerFields) {
        const inField1 = field.some(keyword => c1.includes(keyword));
        const inField2 = field.some(keyword => c2.includes(keyword));
        if (inField1 && inField2) return 0.7;
      }
      
      return 0;
    } catch (error) {
      return 0;
    }
  };

  const calculateEducationCompatibility = (education1, education2) => {
    if (!education1 || !education2 || 
        typeof education1 !== 'string' || typeof education2 !== 'string') {
      return 0;
    }
    
    try {
      const e1 = education1.toLowerCase().trim();
      const e2 = education2.toLowerCase().trim();
      
      // Exact match
      if (e1 === e2) return 1;
      
      // Education level matching
      const levels = {
        'high': ['high school', 'secondary', 'diploma'],
        'bachelor': ['bachelor', 'undergraduate', 'college'],
        'master': ['master', 'mba', 'graduate'],
        'phd': ['phd', 'doctorate', 'doctoral']
      };
      
      for (const [, keywords] of Object.entries(levels)) {
        const hasLevel1 = keywords.some(keyword => e1.includes(keyword));
        const hasLevel2 = keywords.some(keyword => e2.includes(keyword));
        if (hasLevel1 && hasLevel2) return 0.8;
      }
      
      return 0;
    } catch (error) {
      return 0;
    }
  };


  const toggleReaction = async (postId, reactionType) => {
    if (!user) {
      showToast(t('messages.loginToReact'), 'error');
      return;
    }

    try {
      // Call backend API to toggle reaction
      const response = await fetch(`${API_URL}/posts/reactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.userId,
          postId: postId,
          reactionType: reactionType
        })
      });

      if (!response.ok) {
        showToast('Reactions feature not available yet', 'error');
        return;
      }

      // Refresh reactions for this post
      await fetchPostReactions(postId);
      
    } catch (error) {
      showToast('Reactions feature not available yet', 'error');
    }
  };

  const toggleComments = async (postId) => {
    // Open comments clip instead of inline comments
    if (commentsClipPost === postId) {
      // If already showing this post's comments in clip, close it
      setCommentsClipPost(null);
    } else {
      // Show this post's comments in clip
      setCommentsClipPost(postId);
      
      // Fetch comments if not already loaded
      if (!postComments[postId]) {
        fetchPostComments(postId);
      }
    }
  };

  const handleCommentChange = (postId, value) => {
    setNewComment(prev => ({
      ...prev,
      [postId]: value
    }));
  };

  const addComment = async (postId) => {
    if (!user) {
      showToast(t('messages.loginToComment'), 'error');
      return;
    }

    const commentText = newComment[postId]?.trim();
    if (!commentText) {
      showToast('Please enter a comment', 'error');
      return;
    }

    try {
      // Call backend API to add comment
      const response = await fetch(`${API_URL}/posts/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.userId,
          postId: postId,
          content: commentText
        })
      });

      if (!response.ok) {
        showToast('Comments feature not available yet', 'error');
        return;
      }

      await response.json();
      
      // Clear the comment input
      setNewComment(prev => ({
        ...prev,
        [postId]: ''
      }));
      
      // showToast(t('messages.addedComment'), 'success');
      
      // Refresh comments for this post
      await fetchPostComments(postId);
      
    } catch (error) {
      showToast('Comments feature not available yet', 'error');
    }
  };

  const getPostComments = (postId) => {
    // Get comments from state instead of localStorage
    return postComments[postId] || [];
  };

  const fetchPostReactions = useCallback(async (postId) => {
    if (!postId) return;
    
    try {
      const response = await fetch(`${API_URL}/posts/reactions/${postId}`);
      
      if (!response.ok) {
        // If API doesn't support reactions yet, set default values
        setPostReactions(prev => ({
          ...prev,
          [postId]: {
            likes: 0,
            loves: 0,
            userReactions: { hasLiked: false, hasLoved: false }
          }
        }));
        return;
      }
      
      const data = await response.json();
      setPostReactions(prev => ({
        ...prev,
        [postId]: {
          likes: data.reactions?.likes || 0,
          loves: data.reactions?.loves || 0,
          userReactions: data.reactions?.userReactions?.[user?.userId] || { hasLiked: false, hasLoved: false }
        }
      }));
    } catch (error) {
      // Set default values if API call fails
      setPostReactions(prev => ({
        ...prev,
        [postId]: {
          likes: 0,
          loves: 0,
          userReactions: { hasLiked: false, hasLoved: false }
        }
      }));
    }
  }, [user]);

  const fetchPostComments = useCallback(async (postId) => {
    if (!postId) return;
    
    try {
      const response = await fetch(`${API_URL}/posts/comments/${postId}`);
      
      if (!response.ok) {
        // If API doesn't support comments yet, set empty array
        setPostComments(prev => ({
          ...prev,
          [postId]: []
        }));
        return;
      }
      
      const data = await response.json();
      setPostComments(prev => ({
        ...prev,
        [postId]: data.comments || []
      }));
    } catch (error) {
      // Set empty array if API call fails
      setPostComments(prev => ({
        ...prev,
        [postId]: []
      }));
    }
  }, []);


  // Function to get user profile for avatar display
  const getUserProfile = useCallback(async (userId) => {
    if (userProfilesCache[userId]) {
      return userProfilesCache[userId];
    }

    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      } else {
        console.warn('getUserProfile: No token found, skipping profile fetch');
        return null;
      }
      
      const response = await fetch(`${API_URL}/profiles/${userId}`, { headers });
      
      if (!response.ok) {
        if (response.status === 401) {
          console.warn('Authentication failed - user may need to log in again');
            localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        } else if (response.status === 500) {
          console.warn(`Server error fetching profile for user ${userId}, using fallback`);
        } else {
          console.warn(`Failed to fetch profile for user ${userId}: ${response.status}`);
        }
        return null;
      }
      
      const data = await response.json();
      
      if (data.profile) {
        setUserProfilesCache(prev => ({
          ...prev,
          [userId]: data.profile
        }));
        return data.profile;
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
    
    return null;
  }, [userProfilesCache]);

  // Get cached user profile or fallback data
  const getCachedUserProfile = (userId, userName, userEmail) => {
    return userProfilesCache[userId] || {
      userId,
      name: userName || userEmail || 'Unknown User',
      email: userEmail,
      photos: []
    };
  };

  // Load reactions and comments when posts are fetched
  useEffect(() => {
    if (posts.length > 0 && user) {
      posts.forEach(post => {
        fetchPostReactions(post.postId);
        fetchPostComments(post.postId);
        // Fetch user profile for post author avatar
        if (post.userId && !userProfilesCache[post.userId]) {
          getUserProfile(post.userId);
        }
      });
    }
  }, [posts, user, userProfilesCache, fetchPostReactions, fetchPostComments, getUserProfile]);

  // Load user profiles for comments when they are fetched
  useEffect(() => {
    Object.values(postComments).forEach(comments => {
      comments.forEach(comment => {
        if (comment.userId && !userProfilesCache[comment.userId]) {
          getUserProfile(comment.userId);
        }
      });
    });
  }, [postComments, userProfilesCache, getUserProfile]);




  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check photo limit for editing profile (3 max)
      if (editingProfile && editingProfile.photos && editingProfile.photos.length >= 3) {
        showToast('Maximum 3 photos allowed per profile. Please delete a photo first.', 'error');
        e.target.value = ''; // Reset file input
        return;
      }

      // Validate file type (including HEIC support)
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
      const allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.heif'];
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      
      if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
        showToast('Please select a valid image file (JPG, PNG, GIF, WebP, HEIC)', 'error');
        return;
      }

      // Compress image if it's too large
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        showToast(`Compressing ${file.name}...`, 'info');
        try {
          const compressed = await compressImage(file, 8192); // Target ~8MB max
          
          // Create a new File object from the compressed blob
          const compressedFile = new File([compressed.blob], file.name, {
            type: compressed.blob.type,
            lastModified: Date.now()
          });
          
          setSelectedPhoto(compressedFile);
          setPhotoPreview(compressed.preview);
          
          if (compressed.compressed) {
            showToast(`${file.name} compressed successfully`, 'success');
          }
        } catch (error) {
          showToast(`Error compressing ${file.name}`, 'error');
          console.error('Compression error:', error);
          return;
        }
      } else {
        // Image is already small enough
        setSelectedPhoto(file);
        
        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => setPhotoPreview(e.target.result);
        reader.readAsDataURL(file);
      }
    }
  };

  const uploadPhoto = async (userId) => {
    if (!selectedPhoto) return null;

    setUploadingPhoto(true);
    try {
      // Convert file to base64
      const reader = new FileReader();
      return new Promise((resolve, reject) => {
        reader.onload = async () => {
          try {
            const base64Data = reader.result.split(',')[1]; // Remove data:image/jpeg;base64, prefix
            
            const photoPayload = {
              userId: userId,
              photoBase64: base64Data,
              fileName: selectedPhoto.name
            };


            const token = localStorage.getItem('token');
            const headers = {
              'Content-Type': 'application/json',
            };
            
            if (token) {
              headers.Authorization = `Bearer ${token}`;
            }

            const response = await fetch(`${API_URL}/photos`, {
              method: 'POST',
              headers,
              body: JSON.stringify(photoPayload)
            });

            const data = await response.json();
            
            if (response.ok) {
              // Update user data with photo information
              if (data.profile) {
                const currentUser = JSON.parse(localStorage.getItem('user'));
                
                // Add cache-busting timestamp to photo URLs
                const photosWithTimestamp = data.profile.photos.map(url => 
                  url.includes('?') ? url : `${url}?t=${Date.now()}`
                );
                
                const updatedUser = { ...currentUser, photos: photosWithTimestamp };
                
                localStorage.setItem('user', JSON.stringify(updatedUser));
                setUser(updatedUser);
                
                // Also update userProfile if it exists and matches current user
                if (currentUser.userId === updatedUser.userId) {
                  setUserProfile(prev => prev ? { ...prev, photos: photosWithTimestamp } : null);
                }
              }
              resolve(data.photoUrl);
            } else {
              reject(new Error(data.message || 'Failed to upload photo'));
            }
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(selectedPhoto);
      });
    } catch (error) {
      showToast('Failed to upload photo: ' + error.message, 'error');
      return null;
    } finally {
      setUploadingPhoto(false);
    }
  };


  const showToast = (msg, type = 'success') => {
    const id = Date.now() + Math.random();
    const newToast = { id, message: msg, type };
    
    setToasts(prev => [...prev, newToast]);
    
    // Auto remove after 4 seconds
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 4000);
  };

  const showErrorToast = (msg) => {
    const now = Date.now();
    
    // Prevent any error messages within 3 seconds of the last one
    if (now - lastErrorTimeRef.current < 3000) {
      return;
    }
    
    // Also prevent duplicate error messages within 8 seconds
    if (recentErrorsRef.current.has(msg)) {
      return;
    }
    
    lastErrorTimeRef.current = now;
    recentErrorsRef.current.add(msg);
    showToast(msg, 'error');
    
    // Clear the error from recent errors after 8 seconds
    setTimeout(() => {
      recentErrorsRef.current.delete(msg);
    }, 8000);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const showAuthMessage = (msg, type) => {
    setAuthMessage(msg);
    setAuthMessageType(type);
    setTimeout(() => {
      setAuthMessage('');
      setAuthMessageType('');
    }, 5000);
  };

  // Check for existing session on load
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const userMenu = document.getElementById('user-menu');
      const userAvatar = document.querySelector('.user-avatar');
      
      if (userMenu && userAvatar && 
          !userMenu.contains(event.target) && 
          !userAvatar.contains(event.target)) {
        userMenu.classList.remove('show');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Authentication functions
  const handleAuthInputChange = (e) => {
    const { name, value } = e.target;
    setAuthData(prev => ({
      ...prev,
      [name]: value
    }));
    
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Validate email or phone
    const validation = validateEmailOrPhone(authData.email);
    if (!validation.isValid) {
      showAuthMessage(t('auth.invalidEmailOrPhone'), 'error');
      setLoading(false);
      return;
    }
    
    try {
      // Prepare login data based on input type
      const loginData = {
        password: authData.password
      };
      
      if (validation.type === 'email') {
        loginData.email = authData.email;
      } else {
        loginData.phone = authData.email; // Using email field but it's actually phone
      }
      
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData)
      });

      const data = await response.json();
      
      if (response.ok) {
        // Clear any previous user's profile states first
        setEditingProfile(null);
        setViewingProfile(null);
        setUserProfile(null);
        setSelectedPhoto(null);
        setPhotoPreview(null);
        setAllUsers([]);
        setProfiles([]);
        setMatches([]);
        
        
        // Set token FIRST, then user to ensure token is available for any immediate API calls
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        // Set user state after token is saved
        setUser(data.user);
        setShowAuth(false);
        setAuthData({ email: '', password: '', name: '' });
        setShowPassword(false);
        setAuthMessage('');
        setAuthMessageType('');
        
        // Clear the fetched tabs to prevent redundant calls during login
        fetchedTabsRef.current.clear();
        
        // showToast('Welcome back!', 'success');
      } else {
        showAuthMessage(data.message || 'Login failed', 'error');
      }
    } catch (error) {
      showAuthMessage('Error connecting to server', 'error');
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validate email or phone
    const validation = validateEmailOrPhone(authData.email);
    if (!validation.isValid) {
      showAuthMessage(t('auth.invalidEmailOrPhone'), 'error');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    
    try {
      // Prepare registration data based on input type
      const registerData = {
        password: authData.password,
        name: authData.name
      };
      
      if (validation.type === 'email') {
        registerData.email = authData.email;
      } else {
        registerData.phone = authData.email; // Using email field but it's actually phone
      }
      
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registerData)
      });

      const data = await response.json();
      
      if (response.ok) {
        // Set token FIRST, then user to ensure token is available for any immediate API calls
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        setUser(data.user);
        setShowAuth(false);
        setAuthData({ email: '', password: '', name: '' });
        setShowPassword(false);
        setAuthMessage('');
        setAuthMessageType('');
        // Clear any previous user's profile states
        setEditingProfile(null);
        setViewingProfile(null);
        setUserProfile(null);
        setSelectedPhoto(null);
        setPhotoPreview(null);
        // showToast('Welcome to Let\'s Connect! Your profile has been created.', 'success');
        // Refresh profiles to include the new user's profile
        fetchProfiles();
      } else {
        showAuthMessage(data.message || 'Registration failed', 'error');
      }
    } catch (error) {
      showAuthMessage('Error connecting to server', 'error');
    }
    setLoading(false);
  };

  // Register Profile functions
  const handleRegisterProfileInputChange = (e) => {
    const { name, value } = e.target;
    setRegisterProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Clear register profile form
  const clearRegisterProfileForm = () => {
    setRegisterProfileData({
      name: '', email: '', password: '', gender: '', age: '', height: '', weight: '',
      maritalStatus: '', career: '', personality: '', education: '', languages: '',
      interests: '', bornPlace: '', location: '', expectation: '', phone: ''
    });
    setSelectedPhoto(null);
    setPhotoPreview(null);
    setAuthMessage('');
    setAuthMessageType('');
    setShowPassword(false);
  };

  const handleRegisterProfileSubmit = async (e) => {
    e.preventDefault();
    const startTime = performance.now();
    setLoading(true);
    
    // Validate email or phone
    const validation = validateEmailOrPhone(registerProfileData.email);
    if (!validation.isValid) {
      showAuthMessage(t('auth.invalidEmailOrPhone'), 'error');
      setLoading(false);
      return;
    }

    // Validate that a photo is selected (required)
    if (!selectedPhoto) {
      showAuthMessage(t('messages.profilePhotoRequired'), 'error');
      setLoading(false);
      return;
    }


    try {
      // Create the user account with complete profile data in one request
      const registerData = {
        password: registerProfileData.password,
        name: registerProfileData.name,
        // Include all profile fields in the registration request
        gender: registerProfileData.gender,
        age: parseInt(registerProfileData.age),
        height: registerProfileData.height,
        weight: registerProfileData.weight,
        maritalStatus: registerProfileData.maritalStatus,
        career: registerProfileData.career,
        personality: registerProfileData.personality,
        education: registerProfileData.education,
        languages: registerProfileData.languages ? registerProfileData.languages.split(',').map(l => l.trim()).filter(l => l) : [],
        interests: registerProfileData.interests ? registerProfileData.interests.split(',').map(i => i.trim()).filter(i => i) : [],
        bornPlace: registerProfileData.bornPlace,
        location: registerProfileData.location,
        expectation: registerProfileData.expectation,
        phone: registerProfileData.phone
      };
      
      if (validation.type === 'email') {
        registerData.email = registerProfileData.email;
      } else {
        // If phone was entered in email field, use that as the primary phone unless a separate phone was provided
        registerData.phone = registerProfileData.phone || registerProfileData.email; // Using email field but it's actually phone
      }

      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registerData)
      });

      const data = await response.json();
      
      if (response.ok) {
        // Set token and user first
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        setUser(data.user);

        // If there's a photo to upload, upload it
        let photoUploaded = false;
        if (selectedPhoto) {
          try {
            const photoUrl = await uploadPhoto(data.user.userId);
            if (photoUrl) {
              photoUploaded = true;
              // showToast('Profile and photo uploaded successfully!', 'success');
            }
          } catch (error) {
            console.error('Photo upload failed:', error);
            showToast('Profile created but photo upload failed. You can add photos later.', 'warning');
          }
        }

        // Update user state with latest data from localStorage (includes photos if uploaded)
        const finalUser = JSON.parse(localStorage.getItem('user') || '{}');
        setUser(finalUser);
        
        // Set userProfile to match the current user so profile tab shows photos immediately
        setUserProfile(finalUser);

        // Clear form and state
        setShowRegisterProfile(false);
        clearRegisterProfileForm();
        
        if (!photoUploaded && !selectedPhoto) {
          // showToast('Welcome to Let\'s Connect! Your complete profile has been created.', 'success');
        }
        
        // Skip expensive fetchProfiles() call - user is already set up
        // fetchProfiles() will be called later when user navigates to discover/matches
      } else {
        showAuthMessage(data.message || 'Registration failed', 'error');
      }
    } catch (error) {
      showAuthMessage('Error connecting to server', 'error');
    }
    setLoading(false);
  };

  const handleLogout = () => {
    // Clear all user-related state
    setUser(null);
    setUserProfile(null);
    setProfiles([]);
    setAllUsers([]);
    setMatches([]);
    setUserProfilesCache({});
    setPosts([]);
    setPostReactions({});
    setPostComments({});
    
    // Clear localStorage
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    
    // Clear UI state
    setEditingProfile(null);
    setViewingProfile(null);
    setSelectedPhoto(null);
    setPhotoPreview(null);
    setActiveTab('home'); // Reset to home tab
    
    // showToast('Logged out successfully', 'success');
  };

  // Language switching function
  const toggleLanguage = () => {
    const newLanguage = i18n.language === 'en' ? 'vi' : 'en';
    i18n.changeLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
    document.getElementById('user-menu').classList.remove('show');
    // showToast(newLanguage === 'vi' ? 'Chuyển sang Tiếng Việt' : 'Switched to English', 'success');
  };

  // View profile function
  const viewProfile = (profile) => {
    setViewingProfile(profile);
  };

  // Edit profile functions
  const startEditing = (profile) => {
    setEditingProfile(profile);
    setEditFormData({
      gender: profile.gender || '',
      age: profile.age ? profile.age.toString() : '',
      height: profile.height || '',
      weight: profile.weight || '',
      maritalStatus: profile.maritalStatus || '',
      career: profile.career || '',
      personality: profile.personality || '',
      education: profile.education || '',
      languages: Array.isArray(profile.languages) ? profile.languages.join(', ') : (profile.languages || ''),
      interests: Array.isArray(profile.interests) ? profile.interests.join(', ') : (profile.interests || ''),
      bornPlace: profile.bornPlace || '',
      location: profile.location || '',
      expectation: profile.expectation || '',
      phone: profile.phone || ''
    });
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingProfile) return;

    setLoading(true);
    try {
      // First, upload photo if one is selected
      let successMessage = '';
      let hasPhotoUpload = !!selectedPhoto;
      
      if (selectedPhoto) {
        setUploadingPhoto(true);
        try {
          const photoUrl = await uploadPhoto(editingProfile.userId);
          if (photoUrl) {
            successMessage = hasPhotoUpload ? 'Profile and photo updated successfully!' : 'Profile updated successfully!';
          }
        } catch (error) {
          successMessage = 'Profile updated successfully, but photo upload failed. Please try uploading the photo again.';
        }
        setUploadingPhoto(false);
      } else {
        successMessage = 'Profile updated successfully!';
      }

      // Then update the profile data
      const updateData = {
        ...editFormData,
        age: editFormData.age ? parseInt(editFormData.age) : null,
        interests: editFormData.interests ? editFormData.interests.split(',').map(i => i.trim()).filter(i => i) : [],
        languages: editFormData.languages ? editFormData.languages.split(',').map(l => l.trim()).filter(l => l) : []
      };

      const response = await fetch(`${API_URL}/profiles/${editingProfile.userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData)
      });

      const data = await response.json();
      
      if (response.ok) {
        // showToast(successMessage, 'success');
        
        // Clear photo selection
        setSelectedPhoto(null);
        setPhotoPreview(null);
        
        // Reset file inputs
        const editInput = document.getElementById('edit-photo-input');
        if (editInput) editInput.value = '';
        
        // Close the edit modal
        setEditingProfile(null);
        
        fetchProfiles(); // Refresh profiles list
        if (activeTab === 'profile') {
          fetchUserProfile(); // Refresh user's own profile
        }
        
        // Clear cached profile for the updated user to force refresh
        setUserProfilesCache(prev => {
          const updated = {...prev};
          delete updated[editingProfile.userId];
          return updated;
        });
        
        // Immediately refresh the user's profile in cache
        getUserProfile(editingProfile.userId);
      } else {
        showToast(data.message || 'Failed to update profile', 'error');
      }
    } catch (error) {
      showErrorToast('Error connecting to server');
    }
    setLoading(false);
  };

  const deletePhoto = async (photoUrl) => {
    if (!editingProfile || !window.confirm('Delete this photo?')) return;

    try {
      const response = await fetch(`${API_URL}/photos/${editingProfile.userId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ photoUrl })
      });

      const data = await response.json();
      
      if (response.ok) {
        // showToast('Photo deleted successfully!', 'success');
        // Update the editing profile with new photos
        setEditingProfile(data.profile);
        fetchProfiles(); // Refresh profiles list
      } else {
        showToast(data.message || 'Failed to delete photo', 'error');
      }
    } catch (error) {
      showToast('Error deleting photo', 'error');
    }
  };


  // Function to toggle user roles
  const toggleUserRole = async (email, roleType) => {
    try {
      const payload = { 
        email: email,
        [`is${roleType.charAt(0).toUpperCase() + roleType.slice(1)}`]: true 
      };
      
      const response = await fetch(`${API_URL}/profiles/staff`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      
      if (response.ok) {
        // showToast(`User role updated successfully!`, 'success');
        fetchAllUsers(); // Refresh the all users list for executive page
        fetchUserProfile(); // Also refresh user profile to update current user's role display
      } else {
        showToast(data.message || `Failed to update user role: ${response.status}`, 'error');
      }
    } catch (error) {
      showToast(`Error updating user role: ${error.message}`, 'error');
    }
  };

  // Improved search function with debouncing and better matching
  const searchUsers = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      // Normalize search query for better matching
      const normalizedQuery = query.toLowerCase().trim();
      const searchTerms = normalizedQuery.split(/\s+/).filter(term => term.length > 0);
      
      // Search within all users array with improved filtering
      const filtered = allUsers.filter(user => {
        // Enhanced search matching
        const searchableFields = [
          user.name || '',
          user.email || '',
          user.phone || '',
          user.userId || '',
          user.career || '',
          user.location || ''
        ].map(field => field.toLowerCase());
        
        // Check if all search terms match any field (AND logic for multiple terms)
        const matchesQuery = searchTerms.every(term => 
          searchableFields.some(field => field.includes(term))
        );
        
        if (!matchesQuery) return false;
        
        // Role-based access control: admin and mod users cannot see executive users
        const userRoles = getUserRoles();
        if ((userRoles.isAdmin || userRoles.isMod) && !userRoles.isExecute) {
          // Admin and mod users (who are not executives) cannot see executive users
          if (user.isExecute) return false;
        }
        
        return true;
      });
      
      // Sort results by relevance (exact name matches first, then email, then others)
      const sortedResults = filtered.sort((a, b) => {
        const aName = (a.name || '').toLowerCase();
        const bName = (b.name || '').toLowerCase();
        const aEmail = (a.email || '').toLowerCase();
        const bEmail = (b.email || '').toLowerCase();
        
        // Exact name matches first
        if (aName.includes(normalizedQuery) && !bName.includes(normalizedQuery)) return -1;
        if (!aName.includes(normalizedQuery) && bName.includes(normalizedQuery)) return 1;
        
        // Then email matches
        if (aEmail.includes(normalizedQuery) && !bEmail.includes(normalizedQuery)) return -1;
        if (!aEmail.includes(normalizedQuery) && bEmail.includes(normalizedQuery)) return 1;
        
        // Finally alphabetical by name
        return aName.localeCompare(bName);
      });
      
      setSearchResults(sortedResults);
    } catch (error) {
      showToast(t('messages.errorSearchingUsers'), 'error');
    } finally {
      setSearchLoading(false);
    }
  };
  
  // Debounced search function
  const debouncedSearch = (query) => {
    // Clear previous timer
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }
    
    // Set new timer
    const newTimer = setTimeout(() => {
      searchUsers(query);
    }, 300); // 300ms delay
    
    setSearchDebounceTimer(newTimer);
  };
  
  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    if (value.trim()) {
      debouncedSearch(value);
    } else {
      setSearchResults([]);
      setSearchLoading(false);
    }
  };
  
  
  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setSearchLoading(false);
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
      setSearchDebounceTimer(null);
    }
  };

  // Toggle user account activation
  const toggleUserActivation = async (userId, currentStatus) => {
    try {
      // For now, we'll use the existing endpoint structure
      // TODO: Create a dedicated endpoint for user activation/deactivation
      const response = await fetch(`${API_URL}/profiles/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          isActive: !currentStatus
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        // showToast(`User ${!currentStatus ? 'activated' : 'deactivated'} successfully!`, 'success');
        fetchAllUsers(); // Refresh the users list
        // Update search results if showing
        if (searchQuery.trim()) {
          debouncedSearch(searchQuery);
        }
      } else {
        showToast(data.message || `Failed to ${!currentStatus ? 'activate' : 'deactivate'} user`, 'error');
      }
    } catch (error) {
      showToast(`Error updating user status: ${error.message}`, 'error');
    }
  };

  // Helper function to translate marital status values
  const getTranslatedMaritalStatus = (status) => {
    if (!status) return t('profile.noMaritalStatus');
    
    const statusKey = `forms.${status}`;
    const translated = t(statusKey);
    
    // If translation key doesn't exist, return the original value
    return translated === statusKey ? status : translated;
  };

  // Helper function to translate gender values
  const getTranslatedGender = (gender) => {
    if (!gender) return t('profile.genderNotSpecified');
    
    const genderKey = `forms.${gender}`;
    const translated = t(genderKey);
    
    // If translation key doesn't exist, return the original value
    return translated === genderKey ? gender : translated;
  };

  // Helper function to check if current user is staff (can see contact info)
  const isCurrentUserStaff = () => {
    const userRoles = getUserRoles();
    return userRoles.isAdmin || userRoles.isMod || userRoles.isExecute;
  };

  // Helper function to check if current user can see someone's phone number
  const canSeePhoneNumber = (profileUserId) => {
    // Owner can always see their own phone number
    if (user && user.userId === profileUserId) {
      return true;
    }
    // Staff can see any phone number
    return isCurrentUserStaff();
  };

  // Helper function to validate email or phone format
  const validateEmailOrPhone = (input) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    // Updated phone regex to allow numbers starting with 0 and international formats
    const phoneRegex = /^[+]?[\d\s\-()]{7,20}$/;
    
    if (emailRegex.test(input)) {
      return { isValid: true, type: 'email' };
    } else if (phoneRegex.test(input.replace(/[\s\-()]/g, ''))) {
      // Additional validation for cleaned phone number
      const cleanedPhone = input.replace(/[\s\-()]/g, '');
      const finalPhoneRegex = /^[+]?[\d]{7,15}$/;
      if (finalPhoneRegex.test(cleanedPhone)) {
        return { isValid: true, type: 'phone' };
      }
    }
    return { isValid: false, type: null };
  };

  // Helper function for improved mobile close button handling
  const handleModalClose = (closeFunction) => {
    return (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      // Prevent multiple rapid clicks
      if (e.target.disabled) return;
      e.target.disabled = true;
      
      // Add a small delay for mobile touch responsiveness
      if (window.navigator.maxTouchPoints > 0) {
        setTimeout(() => {
          closeFunction();
          e.target.disabled = false;
        }, 100);
      } else {
        closeFunction();
        setTimeout(() => {
          e.target.disabled = false;
        }, 100);
      }
    };
  };

  // All useEffect hooks after callback functions are defined
  useEffect(() => {
    if (activeTab === 'browse') {
      const token = localStorage.getItem('token');
      if (token && !fetchedTabsRef.current.has('browse')) {
        fetchProfiles();
        fetchedTabsRef.current.add('browse');
      }
    }
    if (activeTab === 'profile' && (user?.email || user?.userId)) {
      // Always fetch user profile when viewing profile tab if userProfile is missing and not currently loading
      if (!userProfile && !userProfileLoading) {
        fetchUserProfile();
      }
    }
    if (activeTab === 'home') {
      if (!fetchedTabsRef.current.has('home-posts')) {
        fetchPosts();
        fetchedTabsRef.current.add('home-posts');
      }
    }
    if (activeTab === 'matching') {
      if (user) {
        // Only fetch user profile if we don't have one and not currently loading
        if (!userProfile && !userProfileLoading) {
          fetchUserProfile();
        }
        // Only generate matches if we have a token and haven't fetched yet
        const token = localStorage.getItem('token');
        if (token && !fetchedTabsRef.current.has('matching')) {
          generateMatches();
          fetchedTabsRef.current.add('matching');
        }
      }
    }
    if (activeTab === 'settings') {
      const userRoles = getUserRoles();
      if (user && (userRoles.isAdmin || userRoles.isMod || userRoles.isExecute)) {
        fetchAllUsers();
      }
    }
  }, [activeTab, user?.email, userProfile, userProfileLoading]);

  // Watch for user changes and force userProfile refresh
  useEffect(() => {
    if (user) {
      // Only fetch if we don't have a profile and not currently loading
      // The activeTab useEffect will handle fetching based on the current tab
      if (!userProfile && !userProfileLoading) {
        fetchUserProfile();
      }
      // Reset fetched tabs when user changes (except on initial login)
      if (userProfile !== null) {
        fetchedTabsRef.current.clear();
      }
    }
  }, [user?.email, userProfileLoading]);

  // Initialize language on app load
  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (!savedLanguage) {
      i18n.changeLanguage('vi');
      localStorage.setItem('language', 'vi');
    } else {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  // Scroll detection effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 100); // Change color after scrolling 100px
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cleanup search debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }
    };
  }, [searchDebounceTimer]);

  // Lock body scroll when modals are open (comments clip, auth modal)
  useEffect(() => {
    if (commentsClipPost || showAuth) {
      // Store current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    // Cleanup function
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [commentsClipPost, showAuth]);

  // Check for existing session on load
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  // Initialize posts on app load (posts are public content)
  useEffect(() => {
    // Only initialize posts if we haven't fetched them yet and no user is logged in
    // If user is logged in, let the activeTab useEffect handle posts loading
    const storedUser = localStorage.getItem('user');
    if (!fetchedTabsRef.current.has('home-posts') && !storedUser) {
      const initializePosts = async () => {
        setPostsLoading(true);
        try {
          const response = await fetch(`${API_URL}/posts`);
          const data = await response.json();
          
          if (response.ok) {
            setPosts(data.posts || []);
          } else {
            showErrorToast('Failed to fetch posts');
          }
        } catch (error) {
          showErrorToast('Error connecting to server (initializePosts)');
        }
        setPostsLoading(false);
        fetchedTabsRef.current.add('home-posts');
      };
      
      initializePosts();
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const userMenu = document.getElementById('user-menu');
      const userAvatar = document.querySelector('.user-avatar');
      
      if (userMenu && userAvatar && 
          !userMenu.contains(event.target) && 
          !userAvatar.contains(event.target)) {
        userMenu.classList.remove('show');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Lock body scroll when any modal is open
  useEffect(() => {
    const isAnyModalOpen = showRegisterProfile || showAuth || showCreatePost || editingProfile || editingPost || viewingProfile;
    
    if (isAnyModalOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [showRegisterProfile, showAuth, showCreatePost, editingProfile, editingPost, viewingProfile]);

  return (
    <div className="container">
      <div className="header">
        <div className="header-content">
          <div className="header-text">
            <h1>{t('appTitle')}</h1>
            <p>{t('appSubtitle')}</p>
          </div>
          <div className="header-nav">
            {user ? (
              <div className="user-dropdown">
                <div className="user-avatar" onClick={() => document.getElementById('user-menu').classList.toggle('show')}>
                  <Avatar 
                    user={user} 
                    size="medium" 
                    className="header-avatar"
                    key={user?.photos?.[0] || 'no-photo'}
                  />
                </div>
                <div id="user-menu" className="user-menu">
                  <div className="user-menu-header">
                    <Avatar 
                      user={user} 
                      size="small" 
                      className="menu-avatar"
                      key={user?.photos?.[0] || 'no-photo'}
                    />
                    <div className="user-info">
                      <span className="user-menu-name">{user.name || user.email}</span>
                      <span className="user-menu-email">{user.email}</span>
                    </div>
                  </div>
                  <div className="user-menu-divider"></div>
                  <div className="user-menu-item" onClick={() => {setActiveTab('profile'); document.getElementById('user-menu').classList.remove('show');}}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    {t('profile.myProfile')}
                  </div>
                  <div className="user-menu-item" onClick={() => {setActiveTab('settings'); document.getElementById('user-menu').classList.remove('show');}}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                      <path d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.0113 9.77251C4.28059 9.5799 4.48572 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {t('settings.accountSettings')}
                  </div>
                  <div className="user-menu-item" onClick={toggleLanguage}>
                    <span className="language-flag">
                      {i18n.language === 'en' ? '🇻🇳' : '🇺🇸'}
                    </span>
                    {i18n.language === 'en' ? 'Tiếng Việt' : 'English'}
                  </div>
                  <div className="user-menu-divider"></div>
                  <div className="user-menu-item logout" onClick={() => {handleLogout(); document.getElementById('user-menu').classList.remove('show');}}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <polyline points="16,17 21,12 16,7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    {t('auth.logout')}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <button 
                  className="modern-login-btn"
                  onClick={() => {setShowAuth(true); setAuthMode('login'); setAuthMessage(''); setAuthMessageType('');}}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="10,17 15,12 10,7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="15" y1="12" x2="3" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  {t('auth.login')}
                </button>
                <LanguageSwitcher />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modern Navigation Tabs */}
      <div className={`modern-nav ${isScrolled ? 'scrolled' : ''}`}>
        <div className="nav-tabs">
          <button 
            className={`nav-tab ${activeTab === 'home' ? 'active' : ''}`}
            onClick={() => setActiveTab('home')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="currentColor" strokeWidth="2"/>
              <polyline points="9,22 9,12 15,12 15,22" stroke="currentColor" strokeWidth="2"/>
            </svg>
            <span>{t('nav.home')}</span>
          </button>
          <button 
            className={`nav-tab ${activeTab === 'browse' ? 'active' : ''}`}
            onClick={() => setActiveTab('browse')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
              <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>{t('nav.discover')}</span>
          </button>
          {user && getUserRoles().isAdmin && (
            <button 
              className={`nav-tab ${activeTab === 'matching' ? 'active' : ''}`}
              onClick={() => setActiveTab('matching')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>{t('nav.matching')}</span>
            </button>
          )}
        </div>
      </div>

      {activeTab === 'home' && (
        <div className="home-container">
          {user && (() => {
            const userRoles = getUserRoles();
            return userRoles.isAdmin || userRoles.isExecute || userRoles.isMod;
          })() && (
            <div className="home-header">
              <button 
                className="create-post-btn"
                onClick={() => setShowCreatePost(true)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                {t('buttons.createPost')}
              </button>
            </div>
          )}
          
          {postsLoading ? (
            <div className="loading">Loading posts...</div>
          ) : (
            <>
              {posts.length === 0 ? (
                <div className="no-posts">
                  <div className="no-posts-icon">📝</div>
                  <h3>{t('posts.noPosts')}</h3>
                  <p>{t('posts.beFirst')}</p>
                </div>
              ) : (
                posts.map((post) => (
                  <div key={post.postId} className="post-item">
                    <div className="post-header">
                      <div className="post-author">
                        <Avatar 
                          user={getCachedUserProfile(post.userId, post.userName, '')}
                          size="medium"
                          className="author-avatar"
                        />
                        <div className="author-info">
                          <h4>{getCachedUserProfile(post.userId, post.userName, '').name || post.userName || 'Unknown User'}</h4>
                          <span className="post-time">
                            {new Date(post.createdAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                      </div>
                      {user && (
                        <div className="post-actions-header">
                          {user.userId === post.userId && (
                            <button 
                              className="edit-post-btn-header"
                              onClick={() => startEditingPost(post)}
                              title="Edit Post"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M3 17.25V21H6.75L17.81 9.94L14.06 6.19L3 17.25ZM20.71 7.04C21.1 6.65 21.1 6.02 20.71 5.63L18.37 3.29C17.98 2.9 17.35 2.9 16.96 3.29L15.13 5.12L18.88 8.87L20.71 7.04Z" fill="currentColor"/>
                              </svg>
                            </button>
                          )}
                          {(() => {
                            const userRoles = getUserRoles();
                            return userRoles.isAdmin || userRoles.isExecute || userRoles.isMod;
                          })() && (
                            <button 
                              className="delete-post-btn-header"
                              onClick={() => deletePost(post.postId)}
                              title="Delete Post"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M3 6H5H21M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19ZM10 11V17M14 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="post-content">
                      {post.content && formatPostContent(post.content)}
                      
                      {/* Media Display */}
                      {post.media && post.media.length > 0 && (
                        <div className="post-media">
                          <div className={`media-grid ${post.media.length === 1 ? 'single' : post.media.length === 2 ? 'double' : 'multiple'}`}>
                            {post.media.map((media, index) => (
                              <div key={index} className="media-item">
                                {media.type === 'image' ? (
                                  <img 
                                    src={media.url} 
                                    alt={`Post media ${index + 1}`}
                                    className="post-image"
                                    onClick={() => window.open(media.url, '_blank')}
                                  />
                                ) : (
                                  <video 
                                    src={media.url}
                                    className="post-video"
                                    controls
                                    preload="metadata"
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Reactions and Actions */}
                    <div className="post-actions">
                      <div className="post-reactions">
                        <button 
                          className={`reaction-btn like-btn ${postReactions[post.postId]?.userReactions?.hasLiked ? 'active' : ''}`}
                          onClick={() => toggleReaction(post.postId, 'like')}
                          title="Like this post"
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <span>{postReactions[post.postId]?.likes || 0}</span>
                        </button>
                        
                        <button 
                          className={`reaction-btn love-btn ${postReactions[post.postId]?.userReactions?.hasLoved ? 'active' : ''}`}
                          onClick={() => toggleReaction(post.postId, 'love')}
                          title="Love this post"
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <span>{postReactions[post.postId]?.loves || 0}</span>
                        </button>
                      </div>
                      
                      <button 
                        className="comment-toggle-btn"
                        onClick={() => toggleComments(post.postId)}
                        title="Show/hide comments"
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M21 11.5A8.38 8.38 0 0 1 12.5 20c-1.1 0-2.2-.2-3.2-.6L3 21l1.9-6.3a8.38 8.38 0 0 1-.9-3.8A8.5 8.5 0 1 1 21 11.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>{t('posts.comments')} ({getPostComments(post.postId).length})</span>
                      </button>
                    </div>
                    
                  </div>
                ))
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'browse' && (
        <div className="discover-container">
          {!user ? (
            <div className="login-prompt">
              <h3>{t('browse.pleaseLogin')}</h3>
              <p>{t('browse.needLogin')}</p>
              <button 
                className="btn" 
                onClick={() => setShowAuth(true)}
              >
                {t('auth.login')}
              </button>
            </div>
          ) : loading ? (
            <div className="loading">{t('browse.loadingProfiles')}</div>
          ) : (
            <>
              {profiles.length === 0 ? (
                <div className="no-profiles">
                  <div className="no-profiles-icon">👥</div>
                  <h3>{t('browse.noProfiles')}</h3>
                  <p>Check back later for new connections!</p>
                </div>
              ) : (
                profiles.map((profile) => (
                  <div key={profile.userId} className="profile-item">
                    <div className="profile-photo">
                      {profile.photos && profile.photos.length > 0 ? (
                        <>
                          <img src={profile.photos[0]} alt={profile.name} className="profile-image" />
                          {profile.photos.length > 1 && (
                            <span className="photo-count">+{profile.photos.length - 1} more</span>
                          )}
                        </>
                      ) : (
                        <div className="no-photo">
                          <div className="no-photo-icon">📷</div>
                          <p>{t('browse.noPhoto')}</p>
                        </div>
                      )}
                      
                      {/* Profile info overlay */}
                      <div className="profile-overlay">
                        <div className="profile-header">
                          <h3>{profile.name}, {profile.age}</h3>
                          <p className="basic-info">
                            {getTranslatedGender(profile.gender)}
                            {profile.height && ` • ${profile.height}`}
                            {profile.weight && ` • ${profile.weight}`}
                            {profile.maritalStatus && ` • ${getTranslatedMaritalStatus(profile.maritalStatus)}`}
                          </p>
                        </div>
                        
                        <div className="profile-details">
                          
                          {(profile.career || profile.personality) && (
                            <p className="career-personality">
                              {profile.career && profile.personality ? `${profile.career} • ${profile.personality}` :
                               profile.career || profile.personality}
                            </p>
                          )}
                          
                          {(profile.education || (profile.languages && profile.languages.length > 0)) && (
                            <p className="education-languages">
                              {profile.education && (profile.languages && profile.languages.length > 0) ? 
                                `${profile.education} • ${profile.languages.join(', ')}` :
                                profile.education || (profile.languages && profile.languages.join(', '))}
                            </p>
                          )}
                          
                          {profile.interests && profile.interests.length > 0 && (
                            <p className="profile-interests">
                              {profile.interests.slice(0, 3).join(', ')}
                              {profile.interests.length > 3 && '...'}
                            </p>
                          )}
                          
                          {(profile.bornPlace || profile.location) && (
                            <p className="places">
                              {profile.bornPlace && profile.location ? 
                                `🏠 ${profile.bornPlace} | 📍 ${profile.location}` :
                                profile.bornPlace ? `🏠 ${profile.bornPlace}` : `📍 ${profile.location}`}
                            </p>
                          )}
                          
                          {profile.expectation && (
                            <p className="profile-expectation">"{profile.expectation}"</p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="profile-actions">
                      <button 
                        className="view-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          viewProfile(profile);
                        }}
                        title="View Profile"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z" stroke="currentColor" strokeWidth="2"/>
                          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                      </button>
                      {user && user.email === profile.email && (
                        <button 
                          className="edit-btn"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            startEditing(profile);
                          }}
                          title="Edit Profile"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 17.25V21H6.75L17.81 9.94L14.06 6.19L3 17.25ZM20.71 7.04C21.1 6.65 21.1 6.02 20.71 5.63L18.37 3.29C17.98 2.9 17.35 2.9 16.96 3.29L15.13 5.12L18.88 8.87L20.71 7.04Z" fill="currentColor"/>
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </>
          )}
        </div>
      )}


      {activeTab === 'matching' && user && getUserRoles().isAdmin && (
        <div className="card">
          <div className="matching-header">
            <h2>🔥 Match Analytics</h2>
            <p>AI-powered compatibility analysis based on interests, bio, age, and location</p>
            <button 
              className="refresh-matches-btn"
              onClick={generateMatches}
              disabled={matchesLoading}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <polyline points="23,4 23,10 17,10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="1,20 1,14 7,14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M23 14l-4.64 4.36A9 9 0 0 1 3.51 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {matchesLoading ? 'Analyzing...' : 'Refresh Matches'}
            </button>
          </div>
          
          {matchesLoading ? (
            <div className="loading">Analyzing compatibility between users...</div>
          ) : (
            <div className="matches-container">
              {matches.length === 0 ? (
                <div className="no-matches">
                  <div className="no-matches-icon">💔</div>
                  <h3>No Compatible Matches Found</h3>
                  <p>Users need more complete profiles (bio + interests) for matching</p>
                </div>
              ) : (
                <>
                  <div className="matches-stats">
                    <div className="stat-card">
                      <h4>{matches.length}</h4>
                      <p>Potential Matches</p>
                    </div>
                    <div className="stat-card">
                      <h4>{matches.filter(m => m.compatibility.score >= 70).length}</h4>
                      <p>High Compatibility (70%+)</p>
                    </div>
                    <div className="stat-card">
                      <h4>{matches.filter(m => m.compatibility.score >= 50).length}</h4>
                      <p>Good Matches (50%+)</p>
                    </div>
                  </div>
                  
                  <div className="matches-list">
                    {matches.map((match) => (
                      <div key={match.id} className="match-card">
                        <div className="match-header">
                          <div className="match-users">
                            <div className="match-user">
                              <Avatar 
                                user={match.user1}
                                size="medium"
                                className="user-avatar-small"
                              />
                              <div className="user-details">
                                <h4>{match.user1.name}</h4>
                                <span>{match.user1.age} • {match.user1.location || 'No location'}</span>
                              </div>
                            </div>
                            
                            <div className="match-connector">
                              <div className={`compatibility-score ${
                                match.compatibility.score >= 70 ? 'high' : 
                                match.compatibility.score >= 50 ? 'medium' : 'low'
                              }`}>
                                {match.compatibility.score}%
                              </div>
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                            
                            <div className="match-user">
                              <Avatar 
                                user={match.user2}
                                size="medium"
                                className="user-avatar-small"
                              />
                              <div className="user-details">
                                <h4>{match.user2.name}</h4>
                                <span>{match.user2.age} • {match.user2.location || 'No location'}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="match-analysis">
                          <div className="compatibility-factors">
                            <h5>Compatibility Factors:</h5>
                            <div className="factors-list">
                              {match.compatibility.factors.map((factor, index) => (
                                <span key={index} className="factor-tag">{factor}</span>
                              ))}
                            </div>
                          </div>
                          
                          <div className="match-details">
                            <div className="shared-interests">
                              <h6>Common Interests:</h6>
                              <div className="interests-comparison">
                                {(() => {
                                  // Convert interests to arrays if they're strings
                                  const interests1 = Array.isArray(match.user1.interests) 
                                    ? match.user1.interests 
                                    : (typeof match.user1.interests === 'string' 
                                        ? match.user1.interests.split(',').map(i => i.trim()).filter(i => i)
                                        : []);
                                  const interests2 = Array.isArray(match.user2.interests) 
                                    ? match.user2.interests 
                                    : (typeof match.user2.interests === 'string' 
                                        ? match.user2.interests.split(',').map(i => i.trim()).filter(i => i)
                                        : []);
                                  
                                  return interests1.filter(interest => 
                                    interests2.some(i2 => 
                                      i2 && interest && i2.toLowerCase() === interest.toLowerCase()
                                    )
                                  ).map((interest, index) => (
                                    <span key={index} className="shared-interest">{interest}</span>
                                  ));
                                })()}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}


      {activeTab === 'profile' && (user?.email || user?.userId) && (
        <div className="card">
          <h2>{t('profile.myProfile')}</h2>
          <div className="profile-view-content">
            
            {userProfileLoading ? (
              <div className="loading">{t('profile.loadingProfile')}</div>
            ) : (
              <>
                {userProfile ? (
                  <>
                    {/* Profile Photos */}
                    <div className="profile-photos-section">
                      <h3>{t('profile.myPhotos')} ({(userProfile.photos || []).length}/3)</h3>
                      {userProfile.photos && userProfile.photos.length > 0 ? (
                        <div className="photo-gallery">
                          {userProfile.photos.map((photoUrl, index) => (
                            <div key={index} className="gallery-photo">
                              <img src={photoUrl} alt={`${t('profile.photo')} ${index + 1}`} className="gallery-image" />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="no-photos-message">
                          <div className="no-photo-icon">📷</div>
                          <p>{t('profile.noPhotos')}</p>
                        </div>
                      )}
                    </div>

                    {/* Contact Information */}
                    <div className="profile-info-section">
                      <h3>{t('profile.profileDetails')}</h3>
                      <div className="info-grid">
                        <div className="info-item">
                          <label>📧 {t('auth.email')}</label>
                          <span>{user.email}</span>
                        </div>
                        {userProfile.phone && (
                          <div className="info-item">
                            <label>📱 {t('profile.phone')}</label>
                            <span>{userProfile.phone}</span>
                          </div>
                        )}
                        {!userProfile.phone && (
                          <div className="info-item">
                            <label>📱 {t('profile.phone')}</label>
                            <span className="no-contact">{t('profile.notProvided')}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Profile Information */}
                    <div className="profile-info-section">
                      <h3>{t('profile.personalDetails')}</h3>
                      <div className="info-grid">
                        <div className="info-item">
                          <label>{t('profile.age')}</label>
                          <span>{userProfile.age} {t('profile.yearsOld')}</span>
                        </div>
                        <div className="info-item">
                          <label>{t('profile.gender')}</label>
                          <span>{getTranslatedGender(userProfile.gender)}</span>
                        </div>
                        {/* Physical Information */}
                        <div className="info-item">
                          <label>{t('profile.height')}</label>
                          <span>{userProfile.height || t('profile.noHeight')}</span>
                        </div>
                        <div className="info-item">
                          <label>{t('profile.weight')}</label>
                          <span>{userProfile.weight || t('profile.noWeight')}</span>
                        </div>
                        
                        {/* Personal Information */}
                        <div className="info-item">
                          <label>{t('profile.maritalStatus')}</label>
                          <span>{getTranslatedMaritalStatus(userProfile.maritalStatus)}</span>
                        </div>
                        <div className="info-item">
                          <label>{t('profile.career')}</label>
                          <span>{userProfile.career || t('profile.noCareer')}</span>
                        </div>
                        <div className="info-item full-width">
                          <label>{t('profile.personality')}</label>
                          <span>{userProfile.personality || t('profile.noPersonality')}</span>
                        </div>
                        
                        {/* Education and Languages */}
                        <div className="info-item full-width">
                          <label>{t('profile.education')}</label>
                          <span>{userProfile.education || t('profile.noEducation')}</span>
                        </div>
                        <div className="info-item full-width">
                          <label>{t('profile.languages')}</label>
                          {userProfile.languages && userProfile.languages.length > 0 ? (
                            <div className="interests-tags">
                              {userProfile.languages.map((language, index) => (
                                <span key={index} className="interest-tag">{language}</span>
                              ))}
                            </div>
                          ) : (
                            <span>{t('profile.noLanguages')}</span>
                          )}
                        </div>
                        
                        <div className="info-item full-width">
                          <label>{t('profile.interests')}</label>
                          {userProfile.interests && userProfile.interests.length > 0 ? (
                            <div className="interests-tags">
                              {userProfile.interests.map((interest, index) => (
                                <span key={index} className="interest-tag">{interest}</span>
                              ))}
                            </div>
                          ) : (
                            <p>{t('profile.noInterests')}</p>
                          )}
                        </div>
                        
                        {/* Location Information */}
                        <div className="info-item">
                          <label>{t('profile.bornPlace')}</label>
                          <span>{userProfile.bornPlace ? `🏠 ${userProfile.bornPlace}` : t('profile.noBornPlace')}</span>
                        </div>
                        <div className="info-item">
                          <label>{t('profile.location')}</label>
                          <span>{userProfile.location ? `📍 ${userProfile.location}` : t('profile.noLocation')}</span>
                        </div>
                        
                        {/* Expectation (Last) */}
                        <div className="info-item full-width">
                          <label>{t('profile.expectation')}</label>
                          <p>{userProfile.expectation ? `"${userProfile.expectation}"` : t('profile.noExpectation')}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Edit Button */}
                    <div className="profile-actions-section">
                      <div className="profile-action-card">
                        <h4>🎯 {t('profile.editYourProfile')}</h4>
                        <p>{t('profile.updatePhotosDetails')}</p>
                        <button 
                          className="settings-btn"
                          onClick={() => startEditing(userProfile)}
                        >
                          {t('profile.editProfile')}
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="profile-actions-section">
                    <div className="profile-action-card">
                      <h4>📱 {t('profile.createProfile')}</h4>
                      <p>{t('profile.createProfileDesc')}</p>
                      <button 
                        className="settings-btn"
                        onClick={() => {
                          // Refresh user profile data
                          fetchUserProfile();
                        }}
                      >
                        {t('profile.refreshProfile')}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {activeTab === 'settings' && user && (
        <div className="card">
          <h2>{t('settings.accountSettings')}</h2>
          <div className="settings-content">
            <div className="user-profile-section">
              <div className="user-avatar-large">
                <Avatar 
                  user={user}
                  size="large"
                  className="settings-avatar"
                  key={user?.photos?.[0] || 'no-photo'}
                />
              </div>
              <div className="user-details">
                <h3>{user.name || user.email}</h3>
                <p className="user-email-large">{user.email}</p>
                <p className="member-since">{t('settings.memberSince')} {new Date(user.createdAt || Date.now()).toLocaleDateString()}</p>
              </div>
            </div>
            
            <div className="settings-sections">
              <div className="settings-section">
                <h4>🎯 {t('settings.profileManagement')}</h4>
                <p>{t('settings.manageProfile')}</p>
                <button 
                  className="settings-btn"
                  onClick={() => setActiveTab('profile')}
                >
                  {t('profile.viewProfile')}
                </button>
              </div>
              
              <div className="settings-section">
                <h4>🔒 {t('settings.privacySecurity')}</h4>
                <p>{t('settings.controlPrivacy')}</p>
                <div className="privacy-controls">
                  <label className="toggle-option">
                    <input type="checkbox" defaultChecked />
                    <span>{t('settings.showInDiscovery')}</span>
                  </label>
                  <label className="toggle-option">
                    <input type="checkbox" defaultChecked />
                    <span>{t('settings.allowPhotoDownloads')}</span>
                  </label>
                </div>
              </div>
              
              {getUserRoles().isExecute && (
                <div className="settings-section">
                  <h4>👨‍💼 {t('settings.executiveManagement')}</h4>
                  <p>{t('settings.manageUserRoles')}</p>
                  <div className="executive-management">
                    {allUsersLoading ? (
                      <div className="loading-small">{t('settings.loadingUsers')}</div>
                    ) : (
                      <div className="users-list">
                        {allUsers.length === 0 ? (
                          <div className="no-users-small">
                            <p>{t('settings.noUsersToManage')}</p>
                          </div>
                        ) : (
                          allUsers.filter(profile => profile.isAdmin || profile.isExecute || profile.isMod).map((profile) => (
                            <div key={profile.userId} className="user-role-card">
                              <div className="user-role-info">
                                <Avatar 
                                  user={profile}
                                  size="medium"
                                  className="role-avatar"
                                />
                                <div className="user-role-details">
                                  <h4>{profile.name}</h4>
                                  <span className="user-email">{profile.email}</span>
                                  <div className="current-roles">
                                    {profile.isAdmin && <span className="role-badge admin">{t('settings.admin')}</span>}
                                    {profile.isExecute && <span className="role-badge execute">{t('settings.executive')}</span>}
                                    {profile.isMod && <span className="role-badge mod">{t('settings.moderator')}</span>}
                                    {!profile.isAdmin && !profile.isExecute && !profile.isMod && (
                                      <span className="role-badge user">{t('settings.user')}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="role-actions">
                                <button 
                                  className={`role-btn ${profile.isAdmin ? 'active' : ''}`}
                                  onClick={() => toggleUserRole(profile.email, 'admin')}
                                  title={profile.isAdmin ? t('settings.removeAdmin') : t('settings.makeAdmin')}
                                >
                                  {t('settings.admin')}
                                </button>
                                <button 
                                  className={`role-btn ${profile.isMod ? 'active' : ''}`}
                                  onClick={() => toggleUserRole(profile.email, 'mod')}
                                  title={profile.isMod ? t('settings.removeModerator') : t('settings.makeModerator')}
                                >
                                  {t('settings.moderator')}
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {(() => {
                const userRoles = getUserRoles();
                return userRoles.isAdmin || userRoles.isExecute || userRoles.isMod;
              })() && (
                <div className="settings-section">
                  <h4>👥 {t('settings.userManagement')}</h4>
                  <p>{t('settings.searchManageUsers')}</p>
                  <div className="user-management">
                    <div className="search-section">
                      <div className="search-input-group">
                        <div className="search-input-container">
                          <input
                            type="text"
                            placeholder={t('settings.searchUsersImproved')}
                            value={searchQuery}
                            onChange={handleSearchChange}
                            className="search-input"
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') {
                                clearSearch();
                              }
                            }}
                          />
                          {searchQuery && (
                            <button
                              className="clear-search-btn"
                              onClick={clearSearch}
                              title={t('settings.clearSearch')}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          )}
                        </div>
                        {searchLoading && <div className="search-loading">{t('settings.searching')}</div>}
                      </div>
                      
                      {searchQuery && searchResults.length > 0 && (
                        <div className="search-stats">
                          <span className="results-count">
                            {t('settings.searchResultsCount', { count: searchResults.length, query: searchQuery })}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {searchQuery && (
                      <div className="search-results">
                        {searchResults.length === 0 && !searchLoading ? (
                          <div className="no-results">
                            <div className="no-results-icon">🔍</div>
                            <h4>{t('settings.noSearchResults')}</h4>
                            <p>{t('settings.noResultsForQuery', { query: searchQuery })}</p>
                            <div className="search-suggestions">
                              <p>{t('settings.searchSuggestions')}:</p>
                              <ul>
                                <li>{t('settings.searchTipSpelling')}</li>
                                <li>{t('settings.searchTipKeywords')}</li>
                              </ul>
                            </div>
                          </div>
                        ) : (
                          <div className="users-list">
                            {searchResults.map((profile) => (
                              <div key={profile.userId} className="user-management-card">
                                <div className="user-info">
                                  <Avatar 
                                    user={profile}
                                    size="medium"
                                    className="user-avatar"
                                  />
                                  <div className="user-details">
                                    <h4>{profile.name}</h4>
                                    <span className="user-email">{profile.email}</span>
                                    {profile.phone && canSeePhoneNumber(profile.userId) && (
                                      <span className="user-phone">📱 {profile.phone}</span>
                                    )}
                                    {profile.location && (
                                      <span className="user-location">📍 {profile.location}</span>
                                    )}
                                    {profile.career && (
                                      <span className="user-career">💼 {profile.career}</span>
                                    )}
                                    <div className="user-status">
                                      <span className={`status-badge ${profile.isActive ? 'active' : 'inactive'}`}>
                                        {profile.isActive ? t('settings.active') : t('settings.inactive')}
                                      </span>
                                      <div className="user-roles">
                                        {profile.isAdmin && <span className="role-badge admin">{t('settings.admin')}</span>}
                                        {profile.isExecute && <span className="role-badge execute">{t('settings.executive')}</span>}
                                        {profile.isMod && <span className="role-badge mod">{t('settings.moderator')}</span>}
                                        {!profile.isAdmin && !profile.isExecute && !profile.isMod && (
                                          <span className="role-badge user">{t('settings.user')}</span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="user-actions">
                                  {/* Admin, mod, and executives can deactivate accounts. Only executives can deactivate other executives */}
                                  {(() => {
                                    const userRoles = getUserRoles();
                                    return (userRoles.isAdmin || userRoles.isMod || userRoles.isExecute) && 
                                           !(profile.isExecute && !userRoles.isExecute);
                                  })() ? (
                                    <button 
                                      className={`action-btn ${profile.isActive ? 'deactivate' : 'activate'}`}
                                      onClick={() => toggleUserActivation(profile.userId, profile.isActive)}
                                      title={profile.isActive ? t('settings.deactivateAccount') : t('settings.activateAccount')}
                                    >
                                      {profile.isActive ? t('settings.deactivate') : t('settings.activate')}
                                    </button>
                                  ) : (
                                    <span className="restricted-action" title={t('settings.onlyExecutivesCanManage')}>
                                      {(() => {
                                        const userRoles = getUserRoles();
                                        return !(userRoles.isAdmin || userRoles.isMod || userRoles.isExecute) ? t('settings.noPermission') : t('settings.restricted');
                                      })()}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <div className="settings-section danger-zone">
                <h4>⚠️ {t('settings.accountActions')}</h4>
                <p>{t('settings.manageAccount')}</p>
                <button 
                  className="danger-btn"
                  onClick={() => {
                    if (window.confirm(t('settings.confirmLogout'))) {
                      handleLogout();
                    }
                  }}
                >
                  {t('auth.logout')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      {editingProfile && (
        <div className="modal-overlay" onClick={() => setEditingProfile(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('profile.editProfile')} - {editingProfile.name}</h2>
              <button 
                className="modal-close"
                onClick={handleModalClose(() => setEditingProfile(null))}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            
            {/* Current Photos */}
            <div className="current-photos">
              <h3>Photos ({(editingProfile.photos || []).length}/3)</h3>
              <div className="photos-grid">
                {editingProfile.photos && editingProfile.photos.map((photoUrl, index) => (
                  <div key={index} className="photo-item">
                    <img src={photoUrl} alt={`${index + 1}`} className="edit-photo" />
                    <button 
                      type="button"
                      className="delete-photo-btn"
                      onClick={() => deletePhoto(photoUrl)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 6H5H21M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19ZM10 11V17M14 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                ))}
                
                {/* Add Photo Button */}
                {(editingProfile.photos || []).length < 3 && (
                  <div className="photo-item">
                    <button 
                      type="button" 
                      className="photo-icon-btn add-photo-grid-btn"
                      onClick={() => document.getElementById('edit-photo-input').click()}
                      title="Add Photo"
                    >
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                        <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" strokeWidth="2"/>
                        <path d="M21 15L16 10L5 21" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      <div className="plus-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                          <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                        </svg>
                      </div>
                    </button>
                  </div>
                )}
              </div>
              
              <input
                type="file"
                id="edit-photo-input"
                accept="image/*,.heic,.heif"
                onChange={handlePhotoChange}
                style={{display: 'none'}}
              />
              
              {photoPreview && (
                <div className="photo-preview">
                  <div className="preview-photo-container">
                    <img src={photoPreview} alt="Preview" className="preview-image" />
                    <button 
                      type="button"
                      className="delete-photo-btn preview-delete-btn"
                      onClick={() => {
                        setSelectedPhoto(null);
                        setPhotoPreview(null);
                        const editInput = document.getElementById('edit-photo-input');
                        if (editInput) editInput.value = '';
                      }}
                      title="Remove selected photo"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 6H5H21M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19ZM10 11V17M14 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                  <p className="photo-info">Selected: {selectedPhoto?.name}</p>
                  <p className="photo-note">This photo will be uploaded when you update the profile</p>
                </div>
              )}
            </div>

            <form onSubmit={handleEditSubmit}>
              {/* Gender, Age, Height, Weight */}
              <div className="form-row form-row-four">
                <div className="form-group">
                  <label htmlFor="edit-gender">{t('profile.gender')}</label>
                  <select
                    id="edit-gender"
                    name="gender"
                    value={editFormData.gender}
                    onChange={handleEditInputChange}
                    required
                  >
                    <option value="">{t('forms.selectGender')}</option>
                    <option value="male">{t('forms.male')}</option>
                    <option value="female">{t('forms.female')}</option>
                    <option value="other">{t('forms.other')}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="edit-age">{t('profile.age')}</label>
                  <input
                    type="number"
                    id="edit-age"
                    name="age"
                    value={editFormData.age}
                    onChange={handleEditInputChange}
                    min="18"
                    max="100"
                    placeholder="18"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit-height">{t('profile.height')}</label>
                  <input
                    type="text"
                    id="edit-height"
                    name="height"
                    value={editFormData.height}
                    onChange={handleEditInputChange}
                    placeholder={t('forms.heightPlaceholder')}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit-weight">{t('profile.weight')}</label>
                  <input
                    type="text"
                    id="edit-weight"
                    name="weight"
                    value={editFormData.weight}
                    onChange={handleEditInputChange}
                    placeholder={t('forms.weightPlaceholder')}
                  />
                </div>
              </div>

              {/* Personal */}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="edit-maritalStatus">{t('profile.maritalStatus')}</label>
                  <select
                    id="edit-maritalStatus"
                    name="maritalStatus"
                    value={editFormData.maritalStatus}
                    onChange={handleEditInputChange}
                  >
                    <option value="">{t('forms.selectMaritalStatus')}</option>
                    <option value="single">{t('forms.single')}</option>
                    <option value="married">{t('forms.married')}</option>
                    <option value="divorced">{t('forms.divorced')}</option>
                    <option value="widowed">{t('forms.widowed')}</option>
                    <option value="separated">{t('forms.separated')}</option>
                    <option value="complicated">{t('forms.complicated')}</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="edit-career">{t('profile.career')}</label>
                  <input
                    type="text"
                    id="edit-career"
                    name="career"
                    value={editFormData.career}
                    onChange={handleEditInputChange}
                    placeholder={t('forms.careerPlaceholder')}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="edit-personality">{t('profile.personality')}</label>
                <input
                  type="text"
                  id="edit-personality"
                  name="personality"
                  value={editFormData.personality}
                  onChange={handleEditInputChange}
                  placeholder={t('forms.personalityPlaceholder')}
                />
              </div>

              {/* Education & Skills */}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="edit-education">{t('profile.education')}</label>
                  <input
                    type="text"
                    id="edit-education"
                    name="education"
                    value={editFormData.education}
                    onChange={handleEditInputChange}
                    placeholder={t('forms.educationPlaceholder')}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit-languages">{t('profile.languages')}</label>
                  <input
                    type="text"
                    id="edit-languages"
                    name="languages"
                    value={editFormData.languages}
                    onChange={handleEditInputChange}
                    placeholder={t('forms.languagesPlaceholder')}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="edit-interests">{t('profile.interests')}</label>
                <input
                  type="text"
                  id="edit-interests"
                  name="interests"
                  value={editFormData.interests}
                  onChange={handleEditInputChange}
                  placeholder={t('forms.interestsPlaceholder')}
                />
              </div>

              {/* Location */}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="edit-bornPlace">{t('profile.bornPlace')}</label>
                  <input
                    type="text"
                    id="edit-bornPlace"
                    name="bornPlace"
                    value={editFormData.bornPlace}
                    onChange={handleEditInputChange}
                    placeholder={t('forms.bornPlacePlaceholder')}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit-location">{t('profile.location')}</label>
                  <input
                    type="text"
                    id="edit-location"
                    name="location"
                    value={editFormData.location}
                    onChange={handleEditInputChange}
                    placeholder={t('forms.cityCountry')}
                  />
                </div>
              </div>

              {/* Phone */}
              <div className="form-group">
                <label htmlFor="edit-phone">📱 Phone Number</label>
                <input
                  type="tel"
                  id="edit-phone"
                  name="phone"
                  value={editFormData.phone}
                  onChange={handleEditInputChange}
                  placeholder="Enter your phone number"
                />
              </div>

              {/* Expectation */}
              <div className="form-group">
                <label htmlFor="edit-expectation">{t('profile.expectation')}</label>
                <input
                  type="text"
                  id="edit-expectation"
                  name="expectation"
                  value={editFormData.expectation}
                  onChange={handleEditInputChange}
                  placeholder={t('forms.expectationPlaceholder')}
                />
              </div>

              <div className="edit-actions">
                <button type="submit" className="btn" disabled={loading || uploadingPhoto}>
                  {loading ? (uploadingPhoto ? 'Uploading photo...' : 'Updating...') : 
                   selectedPhoto ? t('forms.updateProfilePhoto') : t('forms.updateProfile')}
                </button>
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => setEditingProfile(null)}
                >
                  {t('forms.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Profile Modal */}
      {viewingProfile && (
        <div className="modal-overlay" onClick={() => setViewingProfile(null)}>
          <div className="modal-content profile-view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{viewingProfile.name || t('profile.unknownUser')}</h2>
              <div className="profile-view-actions">
                {user && user.email === viewingProfile.email && (
                  <button 
                    className="edit-profile-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setViewingProfile(null);
                      startEditing(viewingProfile);
                    }}
                    title={t('profile.editProfile')}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 17.25V21H6.75L17.81 9.94L14.06 6.19L3 17.25ZM20.71 7.04C21.1 6.65 21.1 6.02 20.71 5.63L18.37 3.29C17.98 2.9 17.35 2.9 16.96 3.29L15.13 5.12L18.88 8.87L20.71 7.04Z" fill="currentColor"/>
                    </svg>
                    {t('profile.editProfile')}
                  </button>
                )}
                <button 
                  className="modal-close"
                  onClick={handleModalClose(() => setViewingProfile(null))}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Photo Gallery */}
            <div className="profile-photos-section">
              <h3>{t('profile.photos')} ({(viewingProfile.photos || []).length})</h3>
              {viewingProfile.photos && viewingProfile.photos.length > 0 ? (
                <div className="photo-gallery">
                  {viewingProfile.photos.map((photoUrl, index) => (
                    <div key={index} className="gallery-photo">
                      <img 
                        src={photoUrl} 
                        alt={`${t('profile.photo')} ${index + 1}`} 
                        className="gallery-image"
                        onError={(e) => {
                          e.target.style.border = '2px solid red';
                        }}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-photos-message">
                  <div className="no-photo-icon">📷</div>
                  <p>{t('profile.noPhotos')}</p>
                </div>
              )}
            </div>

            {/* Profile Information */}
            <div className="profile-info-section">
              <h3>{viewingProfile.name}, {viewingProfile.age}</h3>
              
              {/* Contact Information - Only visible to staff or owner */}
              {(isCurrentUserStaff() || (user && user.userId === viewingProfile.userId)) && (
                <div className="info-grid">
                  <div className="info-item">
                    <label>📧 {t('auth.email')}</label>
                    <span>{viewingProfile.email || t('profile.notProvided')}</span>
                  </div>
                  {viewingProfile.phone && canSeePhoneNumber(viewingProfile.userId) && (
                    <div className="info-item">
                      <label>📱 {t('profile.phone')}</label>
                      <span>{viewingProfile.phone}</span>
                    </div>
                  )}
                </div>
              )}
              
              <div className="info-grid">
                <div className="info-item">
                  <label>{t('profile.gender')}</label>
                  <span>{getTranslatedGender(viewingProfile.gender)}</span>
                </div>
                {viewingProfile.height && (
                  <div className="info-item">
                    <label>{t('profile.height')}</label>
                    <span>{viewingProfile.height} cm</span>
                  </div>
                )}
                {viewingProfile.weight && (
                  <div className="info-item">
                    <label>{t('profile.weight')}</label>
                    <span>{viewingProfile.weight} kg</span>
                  </div>
                )}
                {viewingProfile.maritalStatus && (
                  <div className="info-item">
                    <label>{t('profile.maritalStatus')}</label>
                    <span>{getTranslatedMaritalStatus(viewingProfile.maritalStatus)}</span>
                  </div>
                )}
                
                {(viewingProfile.career || viewingProfile.personality) && (
                  <div className="info-item full-width">
                    <label>{t('profile.careerPersonality')}</label>
                    <span>
                      {viewingProfile.career && viewingProfile.personality ? 
                        `${viewingProfile.career} • ${viewingProfile.personality}` :
                        viewingProfile.career || viewingProfile.personality}
                    </span>
                  </div>
                )}
                
                {(viewingProfile.education || (viewingProfile.languages && viewingProfile.languages.length > 0)) && (
                  <div className="info-item full-width">
                    <label>{t('profile.educationLanguages')}</label>
                    <span>
                      {viewingProfile.education && (viewingProfile.languages && viewingProfile.languages.length > 0) ? 
                        `${viewingProfile.education} • ${viewingProfile.languages.join(', ')}` :
                        viewingProfile.education || (viewingProfile.languages && viewingProfile.languages.join(', '))}
                    </span>
                  </div>
                )}
                
                {viewingProfile.interests && viewingProfile.interests.length > 0 && (
                  <div className="info-item full-width">
                    <label>{t('profile.interests')}</label>
                    <div className="interests-tags">
                      {viewingProfile.interests.map((interest, index) => (
                        <span key={index} className="interest-tag">{interest}</span>
                      ))}
                    </div>
                  </div>
                )}
                
                {(viewingProfile.bornPlace || viewingProfile.location) && (
                  <div className="info-item full-width">
                    <label>{t('profile.places')}</label>
                    <span>
                      {viewingProfile.bornPlace && viewingProfile.location ? 
                        `🏠 ${viewingProfile.bornPlace} | 📍 ${viewingProfile.location}` :
                        viewingProfile.bornPlace ? `🏠 ${viewingProfile.bornPlace}` : `📍 ${viewingProfile.location}`}
                    </span>
                  </div>
                )}
                
                {viewingProfile.expectation && (
                  <div className="info-item full-width">
                    <label>{t('profile.expectation')}</label>
                    <p>"{viewingProfile.expectation}"</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Authentication Modal */}
      {showAuth && (
        <div className="modal-overlay" onClick={() => {setShowAuth(false); setAuthMessage(''); setAuthMessageType('');}}>
          <div className="modal-content auth-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {authMode === 'login' ? t('auth.welcomeBack') : 
                 authMode === 'register' ? t('auth.joinConnect') :
                 'Account Settings'}
              </h2>
              <button 
                className="modal-close"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowAuth(false); 
                  setAuthMessage(''); 
                  setAuthMessageType('');
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            
            {authMessage && (
              <div className={`${authMessageType}-message`}>
                {authMessage}
              </div>
            )}
            
            {authMode === 'profile' ? (
              <div className="profile-settings">
                <div className="profile-info">
                  <div className="profile-avatar">
                    <Avatar 
                      user={user}
                      size="large"
                      className="auth-avatar"
                      key={user?.photos?.[0] || 'no-photo'}
                    />
                  </div>
                  <h3>{user.name || user.email}</h3>
                  <p className="user-email">{user.email}</p>
                </div>
                <div className="profile-actions">
                  <button 
                    className="profile-action-btn"
                    onClick={() => {setShowAuth(false); handleLogout();}}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <polyline points="16,17 21,12 16,7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    Logout
                  </button>
                </div>
              </div>
            ) : (
            <>
            <form onSubmit={authMode === 'login' ? handleLogin : handleRegister}>
              {authMode === 'register' && (
                <div className="form-group">
                  <label htmlFor="auth-name">{t('auth.fullName')} *</label>
                  <input
                    type="text"
                    id="auth-name"
                    name="name"
                    value={authData.name}
                    onChange={handleAuthInputChange}
                    required
                  />
                </div>
              )}
              
              <div className="form-group">
                <label htmlFor="auth-email">{t('auth.emailOrPhone')} *</label>
                <input
                  type="text"
                  id="auth-email"
                  name="email"
                  value={authData.email}
                  onChange={handleAuthInputChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="auth-password">{t('auth.password')} *</label>
                <div className="password-input-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="auth-password"
                    name="password"
                    value={authData.password}
                    onChange={handleAuthInputChange}
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    title={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              
              <button type="submit" className="btn" disabled={loading}>
                {loading ? t('auth.pleaseWait') : (authMode === 'login' ? t('auth.login') : t('auth.createAccount'))}
              </button>
            </form>
            
            <div className="auth-switch">
              {authMode === 'login' ? (
                <div>
                  {/* Basic sign up disabled - only full profile registration available */}
                  {false && (
                  <p>
                    {t('auth.dontHaveAccount')}{' '}
                    <button 
                      type="button" 
                      className="auth-link"
                      onClick={() => {setAuthMode('register'); setAuthMessage(''); setAuthMessageType('');}}
                    >
                      {t('auth.register')}
                    </button>
                  </p>
                  )}
                  <p style={{marginTop: '10px'}}>
                    {t('auth.wantCompleteProfile')}{' '}
                    <button 
                      type="button" 
                      className="auth-link"
                      onClick={() => {
                        setShowAuth(false); 
                        setShowRegisterProfile(true); 
                        setAuthMessage(''); 
                        setAuthMessageType('');
                      }}
                    >
                      {t('auth.registerFullProfile')}
                    </button>
                  </p>
                </div>
              ) : (
                <div>
                  <p>
                    {t('auth.haveAccount')}{' '}
                    <button 
                      type="button" 
                      className="auth-link"
                      onClick={() => {setAuthMode('login'); setAuthMessage(''); setAuthMessageType('');}}
                    >
                      {t('auth.loginHere')}
                    </button>
                  </p>
                  <p style={{marginTop: '10px'}}>
                    {t('auth.wantCompleteProfile')}{' '}
                    <button 
                      type="button" 
                      className="auth-link"
                      onClick={() => {
                        setShowAuth(false); 
                        setShowRegisterProfile(true); 
                        setAuthMessage(''); 
                        setAuthMessageType('');
                      }}
                    >
                      {t('auth.registerFullProfile')}
                    </button>
                  </p>
                </div>
              )}
            </div>
            </>
            )}
          </div>
        </div>
      )}

      {/* Register Profile Modal */}
      {showRegisterProfile && (
        <div className="modal-overlay" onClick={() => {setShowRegisterProfile(false); clearRegisterProfileForm();}}>
          <div className="modal-content register-profile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('registerProfile.title')}</h2>
              <button 
                className="modal-close"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowRegisterProfile(false); 
                  clearRegisterProfileForm();
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            
            {authMessage && (
              <div className={`${authMessageType}-message`}>
                {authMessage}
              </div>
            )}
            
            <form onSubmit={handleRegisterProfileSubmit} className="register-profile-form">
              {/* Basic Account Information */}
              <div className="form-section">
                <h3>{t('registerProfile.accountInformation')}</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="reg-name" className="required">{t('auth.fullName')}</label>
                    <input
                      type="text"
                      id="reg-name"
                      name="name"
                      value={registerProfileData.name}
                      onChange={handleRegisterProfileInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="reg-email" className="required">{t('auth.emailOrPhone')}</label>
                    <input
                      type="text"
                      id="reg-email"
                      name="email"
                      value={registerProfileData.email}
                      onChange={handleRegisterProfileInputChange}
                      required
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="reg-password" className="required">{t('auth.password')}</label>
                    <div className="password-input-container">
                      <input
                        type={showPassword ? "text" : "password"}
                        id="reg-password"
                        name="password"
                        value={registerProfileData.password}
                        onChange={handleRegisterProfileInputChange}
                        required
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                        title={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                      >
                        {showPassword ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                  {/* <div className="form-group">
                    <label htmlFor="reg-phone">📱 {t('registerProfile.phoneNumber')}</label>
                    <input
                      type="tel"
                      id="reg-phone"
                      name="phone"
                      value={registerProfileData.phone}
                      onChange={handleRegisterProfileInputChange}
                      placeholder={t('registerProfile.phoneNumberPlaceholder')}
                    />
                  </div> */}
                </div>
              </div>

              {/* Photo Upload Section */}
              <div className="form-section">
                <h3 className="required">{t('registerProfile.profilePhoto')}</h3>
                <div className="photo-upload-section">
                  <p>{t('registerProfile.profilePhotoDesc')}</p>
                  
                  <div className="photo-upload-area">
                    {photoPreview ? (
                      <div className="photo-preview-container">
                        <img src={photoPreview} alt="Profile Preview" className="profile-photo-preview" />
                        <button 
                          type="button"
                          className="remove-photo-btn"
                          onClick={() => {
                            setSelectedPhoto(null);
                            setPhotoPreview(null);
                            const regPhotoInput = document.getElementById('reg-photo-input');
                            if (regPhotoInput) regPhotoInput.value = '';
                          }}
                          title={t('registerProfile.removePhoto')}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 6H5H21M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19ZM10 11V17M14 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <button 
                        type="button" 
                        className="upload-photo-btn"
                        onClick={() => document.getElementById('reg-photo-input').click()}
                      >
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                          <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" strokeWidth="2"/>
                          <path d="M21 15L16 10L5 21" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        <span>{t('registerProfile.clickToUpload')}</span>
                        <small>{t('registerProfile.supportsFormats')}</small>
                      </button>
                    )}
                    
                    <input
                      type="file"
                      id="reg-photo-input"
                      accept="image/*,.heic,.heif"
                      onChange={handlePhotoChange}
                      style={{display: 'none'}}
                    />
                  </div>
                  
                  {selectedPhoto && (
                    <div className="photo-info">
                      <p>{t('forms.selectedPhoto')} {selectedPhoto.name}</p>
                      <p>{t('registerProfile.photoWillUpload')}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Basic Profile Information */}
              <div className="form-section">
                <h3>{t('registerProfile.basicInformation')}</h3>
                <div className="form-row form-row-four">
                  <div className="form-group">
                    <label htmlFor="reg-gender" className="required">{t('profile.gender')}</label>
                    <select
                      id="reg-gender"
                      name="gender"
                      value={registerProfileData.gender}
                      onChange={handleRegisterProfileInputChange}
                      required
                    >
                      <option value="">{t('forms.selectGender')}</option>
                      <option value="male">{t('forms.male')}</option>
                      <option value="female">{t('forms.female')}</option>
                      <option value="other">{t('forms.other')}</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="reg-age" className="required">{t('profile.age')}</label>
                    <select
                      id="reg-age"
                      name="age"
                      value={registerProfileData.age}
                      onChange={handleRegisterProfileInputChange}
                      required
                    >
                      <option value="">{t('forms.selectAge')}</option>
                      {Array.from({ length: 83 }, (_, i) => i + 18).map(age => (
                        <option key={age} value={age}>{age}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="reg-height" className="required">{t('profile.height')}</label>
                    <select
                      id="reg-height"
                      name="height"
                      value={registerProfileData.height}
                      onChange={handleRegisterProfileInputChange}
                      required
                    >
                      <option value="">{t('forms.selectHeight')}</option>
                      <option value="1m42">1m42</option>
                      <option value="1m45">1m45</option>
                      <option value="1m47">1m47</option>
                      <option value="1m50">1m50</option>
                      <option value="1m52">1m52</option>
                      <option value="1m55">1m55</option>
                      <option value="1m57">1m57</option>
                      <option value="1m60">1m60</option>
                      <option value="1m63">1m63</option>
                      <option value="1m65">1m65</option>
                      <option value="1m68">1m68</option>
                      <option value="1m70">1m70</option>
                      <option value="1m73">1m73</option>
                      <option value="1m75">1m75</option>
                      <option value="1m78">1m78</option>
                      <option value="1m80">1m80</option>
                      <option value="1m83">1m83</option>
                      <option value="1m85">1m85</option>
                      <option value="1m88">1m88</option>
                      <option value="1m91">1m91</option>
                      <option value="1m93">1m93</option>
                      <option value="1m96">1m96</option>
                      <option value="1m98">1m98</option>
                      <option value="2m01">2m01</option>
                      <option value="2m03">2m03</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="reg-weight" className="required">{t('profile.weight')}</label>
                    <select
                      id="reg-weight"
                      name="weight"
                      value={registerProfileData.weight}
                      onChange={handleRegisterProfileInputChange}
                      required
                    >
                      <option value="">{t('forms.selectWeight')}</option>
                      {Array.from({ length: 111 }, (_, i) => i + 40).map(weight => (
                        <option key={weight} value={`${weight} kg`}>
                          {weight} kg
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Personal Information */}
              <div className="form-section">
                <h3>{t('registerProfile.personalInformation')}</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="reg-maritalStatus">{t('profile.maritalStatus')}</label>
                    <select
                      id="reg-maritalStatus"
                      name="maritalStatus"
                      value={registerProfileData.maritalStatus}
                      onChange={handleRegisterProfileInputChange}
                    >
                      <option value="">{t('forms.selectMaritalStatus')}</option>
                      <option value="single">{t('forms.single')}</option>
                      <option value="married">{t('forms.married')}</option>
                      <option value="divorced">{t('forms.divorced')}</option>
                      <option value="widowed">{t('forms.widowed')}</option>
                      <option value="separated">{t('forms.separated')}</option>
                      <option value="complicated">{t('forms.complicated')}</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="reg-career">{t('profile.career')}</label>
                    <input
                      type="text"
                      id="reg-career"
                      name="career"
                      value={registerProfileData.career}
                      onChange={handleRegisterProfileInputChange}
                      placeholder={t('forms.careerPlaceholder')}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="reg-personality">{t('profile.personality')}</label>
                  <input
                    type="text"
                    id="reg-personality"
                    name="personality"
                    value={registerProfileData.personality}
                    onChange={handleRegisterProfileInputChange}
                    placeholder={t('forms.personalityPlaceholder')}
                  />
                </div>
              </div>

              {/* Education & Skills */}
              <div className="form-section">
                <h3>{t('registerProfile.educationSkills')}</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="reg-education">{t('profile.education')}</label>
                    <input
                      type="text"
                      id="reg-education"
                      name="education"
                      value={registerProfileData.education}
                      onChange={handleRegisterProfileInputChange}
                      placeholder={t('forms.educationPlaceholder')}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="reg-languages">{t('profile.languages')}</label>
                    <input
                      type="text"
                      id="reg-languages"
                      name="languages"
                      value={registerProfileData.languages}
                      onChange={handleRegisterProfileInputChange}
                      placeholder={t('forms.languagesPlaceholder')}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="reg-interests">{t('profile.interests')}</label>
                  <input
                    type="text"
                    id="reg-interests"
                    name="interests"
                    value={registerProfileData.interests}
                    onChange={handleRegisterProfileInputChange}
                    placeholder={t('forms.interestsPlaceholder')}
                  />
                </div>
              </div>

              {/* Location Information */}
              <div className="form-section">
                <h3>{t('registerProfile.locationInformation')}</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="reg-bornPlace">{t('profile.bornPlace')}</label>
                    <input
                      type="text"
                      id="reg-bornPlace"
                      name="bornPlace"
                      value={registerProfileData.bornPlace}
                      onChange={handleRegisterProfileInputChange}
                      placeholder={t('forms.bornPlacePlaceholder')}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="reg-location">{t('profile.location')}</label>
                    <input
                      type="text"
                      id="reg-location"
                      name="location"
                      value={registerProfileData.location}
                      onChange={handleRegisterProfileInputChange}
                      placeholder={t('forms.cityCountry')}
                    />
                  </div>
                </div>
              </div>

              {/* Expectations */}
              <div className="form-section">
                <h3>{t('registerProfile.whatLookingFor')}</h3>
                <div className="form-group">
                  <label htmlFor="reg-expectation">{t('profile.expectation')}</label>
                  <input
                    type="text"
                    id="reg-expectation"
                    name="expectation"
                    value={registerProfileData.expectation}
                    onChange={handleRegisterProfileInputChange}
                    placeholder={t('forms.expectationPlaceholder')}
                  />
                </div>
              </div>

              <div className="register-profile-actions">
                <button type="submit" className="btn" disabled={loading || uploadingPhoto}>
                  {loading ? (uploadingPhoto ? t('registerProfile.uploadingPhoto') : t('registerProfile.creatingProfile')) : 
                   selectedPhoto ? t('registerProfile.createProfileUploadPhoto') : t('registerProfile.createCompleteProfile')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Post Modal */}
      {showCreatePost && (
        <div className="modal-overlay" onClick={() => setShowCreatePost(false)}>
          <div className="modal-content create-post-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('posts.createNewPost')}</h2>
              <button 
                className="modal-close"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowCreatePost(false);
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            
            <form onSubmit={createPost}>
              <div className="form-group">
                <label htmlFor="post-content">{t('posts.shareWithCommunity')}</label>
                <textarea
                  id="post-content"
                  value={newPostContent}
                  onChange={(e) => setNewPostContent(e.target.value)}
                  placeholder="What would you like to share with the community?"
                  rows="3"
                />
                
                {/* Media Upload Section */}
                <div className="media-upload-section">
                  <div className="upload-button-wrapper">
                    <input
                      type="file"
                      id="media-upload"
                      multiple
                      accept="image/*,video/*"
                      onChange={handleMediaSelect}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="media-upload" className="upload-btn">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                        <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" strokeWidth="2"/>
                        <path d="M21 15L16 10L5 21" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      {t('posts.addPhotosVideos')}
                    </label>
                    <span className="upload-hint">Max 4 files: 10MB per image, unlimited video size</span>
                  </div>
                  
                  {/* Media Preview */}
                  {newPostMedia.length > 0 && (
                    <div className="media-preview-grid">
                      {newPostMedia.map((media) => (
                        <div key={media.id} className="media-preview-item">
                          {media.type === 'image' ? (
                            <img src={media.preview} alt={media.name} className="preview-image" />
                          ) : (
                            <video src={media.preview} className="preview-video" muted />
                          )}
                          <button
                            type="button"
                            className="remove-media-btn"
                            onClick={() => removeMediaItem(media.id)}
                            title={t('buttons.removePhoto')}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                          <div className="media-info">
                            <span className="media-name">{media.name}</span>
                            <span className="media-type">{media.type}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
              </div>

              <div className="post-actions">
                <button type="submit" className="btn" disabled={loading || (!newPostContent.trim() && newPostMedia.length === 0)}>
                  {loading ? t('posts.creating') : t('buttons.createPost')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Post Modal */}
      {editingPost && (
        <div className="modal-overlay" onClick={() => setEditingPost(null)}>
          <div className="modal-content create-post-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Post</h2>
              <button 
                className="modal-close"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setEditingPost(null);
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            
            <form onSubmit={updatePost}>
              <div className="form-group">
                <label htmlFor="edit-post-content">Update your post</label>
                <textarea
                  id="edit-post-content"
                  value={editPostContent}
                  onChange={(e) => setEditPostContent(e.target.value)}
                  placeholder="What would you like to share with the community?"
                  rows="6"
                  required
                />
                <div className="post-help">
                  <p>💡 Tips:</p>
                  <ul>
                    <li>Paste YouTube links to embed videos</li>
                    <li>Share thoughts, experiences, or interesting content</li>
                    <li>Keep it friendly and respectful</li>
                  </ul>
                </div>
              </div>

              <div className="post-actions">
                <button type="submit" className="btn" disabled={loading || !editPostContent.trim()}>
                  {loading ? 'Updating...' : 'Update Post'}
                </button>
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => setEditingPost(null)}
                >
                  {t('forms.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Comments Clip Overlay */}
      {commentsClipPost && (
        <div className="comments-clip-overlay" onClick={() => setCommentsClipPost(null)}>
          <div className="comments-clip-content" onClick={(e) => e.stopPropagation()}>
            <div className="comments-clip-header">
              <h3>{t('posts.comments')}</h3>
              <button 
                className="close-clip-btn"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setCommentsClipPost(null);
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            
            <div className="comments-clip-body">
              {/* Comments List */}
              {getPostComments(commentsClipPost).length === 0 ? (
                <div className="no-comments-message">
                  <div className="no-comments-icon">💬</div>
                  <p>{t('posts.noComments')}</p>
                  <span>{t('posts.beFirstComment')}</span>
                </div>
              ) : (
                <div className="comments-clip-list">
                  {getPostComments(commentsClipPost).map((comment, index) => (
                    <div key={index} className="comment-item">
                      <div className="comment-author">
                        <Avatar 
                          user={getCachedUserProfile(comment.userId, comment.userName, '')}
                          size="small"
                          className="comment-avatar"
                        />
                      </div>
                      <div className="comment-content-wrapper">
                        <div className="comment-bubble">
                          <div className="comment-user-name">{getCachedUserProfile(comment.userId, comment.userName, '').name || comment.userName || 'Unknown User'}</div>
                          <div className="comment-content">{comment.content}</div>
                        </div>
                        <div className="comment-time">
                          {new Date(comment.createdAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Add Comment Section */}
            <div className="comments-clip-footer">
              <div className="add-comment">
                <div className="comment-input-container">
                  <Avatar 
                    user={user}
                    size="small"
                    className="comment-input-avatar"
                    key={user?.photos?.[0] || 'no-photo'}
                  />
                  <textarea
                    className="comment-input"
                    placeholder={t('posts.writeComment')}
                    value={newComment[commentsClipPost] || ''}
                    onChange={(e) => handleCommentChange(commentsClipPost, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        addComment(commentsClipPost);
                      }
                    }}
                  />
                  <button
                    className="comment-submit-btn"
                    onClick={() => addComment(commentsClipPost)}
                    disabled={!newComment[commentsClipPost]?.trim()}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 21L23 12L2 3V10L17 12L2 14V21Z" fill="currentColor"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Container */}
      <div className="toast-container">
        {toasts.map((toast) => (
          <div 
            key={toast.id} 
            className={`toast toast-${toast.type}`}
            onClick={() => removeToast(toast.id)}
          >
            <div className="toast-content">
              <div className="toast-icon">
                {toast.type === 'success' ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" fill="currentColor"/>
                    <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" fill="currentColor"/>
                    <path d="M15 9l-6 6M9 9l6 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <span className="toast-message">{toast.message}</span>
              <button 
                className="toast-close"
                onClick={(e) => {
                  e.stopPropagation();
                  removeToast(toast.id);
                }}
              >
                ×
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default App;